import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Observable,
  catchError,
  forkJoin,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';

import { environment } from '../../environments/environment';
import {
  MOCK_CHECKOUTS,
  MOCK_DAMAGED_EQUIPMENT,
  MOCK_EQUIPMENT_ASSETS,
  MOCK_RESERVATIONS,
} from '../data/mock-equipment.data';
import { GenericMultiResultDto } from '../models/api.models';
import { ListQuery, PaginatedResult } from '../models/common.models';
import {
  CheckoutRecord,
  DamagedEquipmentRecord,
  EquipmentAsset,
  EquipmentStats,
  EquipmentStatus,
  Reservation,
} from '../models/equipment.models';
import { ApiService } from './api.service';
import { AuthService } from '../../modules/auth/auth.service';
import { InventoryService } from './inventory.service';
import { SettingsService } from './settings.service';
import {
  asDict,
  firstResultSet,
  findResultSetWithColumn,
  pick,
  pickDate,
  pickNumber,
  pickString,
  resultSetAt,
} from '../utils/api-shape.util';
import {
  resolveBranchId,
  filterRowsByUserBranch,
  branchQueryParams,
  matchesBranchFilter,
  defaultBranchFilterId,
} from '../utils/branch-scope.util';
import { assertCanTake, parsePositiveInt } from '../utils/stock-qty.util';

export interface EquipmentApiResult {
  success: boolean;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class EquipmentService {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly inventory = inject(InventoryService);
  private readonly settings = inject(SettingsService);

  private readonly assets = signal<EquipmentAsset[]>(
    environment.useMockData ? [...MOCK_EQUIPMENT_ASSETS] : [],
  );
  private readonly reservations = signal<Reservation[]>(
    environment.useMockData ? [...MOCK_RESERVATIONS] : [],
  );
  private readonly checkouts = signal<CheckoutRecord[]>(
    environment.useMockData ? [...MOCK_CHECKOUTS] : [],
  );
  private readonly damagedRecords = signal<DamagedEquipmentRecord[]>(
    environment.useMockData ? [...MOCK_DAMAGED_EQUIPMENT] : [],
  );
  /** Reservations issued this session — survives refresh lag for quantity rentals. */
  private readonly issuedReservationIds = signal<Set<string>>(new Set());
  /** Paid reservations on Issue Equipment (includes already checked-out rows). */
  readonly paidReservationsForIssuePage = computed(() =>
    this.buildPaidReservationsForIssuePage(),
  );

  /** @deprecated Alias — ready-to-issue count only. */
  readonly completedReservationsForCheckout = computed(() =>
    this.buildReadyToIssueReservations(),
  );

  readonly allAssets = this.assets.asReadonly();
  readonly allReservations = this.reservations.asReadonly();
  readonly allCheckouts = this.checkouts.asReadonly();
  readonly allDamagedRecords = this.damagedRecords.asReadonly();

  /** Checkoutable register rows linked to active rentable Item Master rows. */
  readonly rentableAssets = computed(() =>
    this.assets().filter((asset) => {
      if (String(asset.id).startsWith('inv-')) {
        return false;
      }
      if (asset.isRentable === false) {
        return false;
      }
      return this.isRegisterAssetBackedByInventory(asset);
    }),
  );

  /** Assets open for new reservations (API CanCheckout or available status). */
  readonly reservableAssets = computed(() =>
    this.rentableAssets().filter((asset) =>
      this.isAssetReservableForReservation(asset),
    ),
  );

  private isAssetReservableForReservation(asset: EquipmentAsset): boolean {
    if (asset.isRentable === false) {
      return false;
    }

    const blocked: EquipmentStatus[] = [
      'checked-out',
      'awaiting-checkout',
      'damaged',
      'lost',
      'in-repair',
    ];
    if (blocked.includes(asset.status)) {
      return false;
    }

    // Prefer mapped availability; CanCheckout can be missing/stale when register status is wrong.
    if (asset.status === 'available') {
      return true;
    }
    if (asset.canCheckout === true) {
      return true;
    }

    return false;
  }

  /** Branch-scoped assets eligible for a new reservation (inventory-backed only). */
  getReservableAssetsForBranch(
    branchId?: string,
    branchName?: string,
  ): EquipmentAsset[] {
    const register = this.filterAssetsByBranch(
      this.reservableAssets(),
      branchId,
      branchName,
    );
    const coveredInventoryIds = new Set(
      this.rentableAssets()
        .map((asset) => asset.inventoryItemID)
        .filter((id): id is number => !!id && id > 0),
    );

    const catalog = this.buildCatalogRentableAssets(
      coveredInventoryIds,
      branchId,
      branchName,
    );
    return this.mergeRegisterAndCatalogAssets(register, catalog);
  }

  /**
   * Reservation equipment picker: all Item Master catalog rows, optionally filtered
   * by store location stock. Without locationId every active catalog item is shown.
   */
  getItemMasterEquipmentForReservation(
    branchId?: string,
    locationId?: string,
    branchName?: string,
  ): EquipmentAsset[] {
    const locationName = locationId
      ? this.settings.scopedLocations().find((row) => row.id === locationId)
          ?.name
      : undefined;

    const catalogItems = this.inventory
      .allItems()
      .filter((item) => this.inventory.isEligibleForEquipmentRental(item))
      .map((item) => this.catalogAssetFromInventoryItem(item));

    let filteredCatalog = catalogItems;
    if (locationId) {
      filteredCatalog = catalogItems.filter((asset) => {
        const invId = asset.inventoryItemID;
        if (!invId) {
          return false;
        }
        return this.inventory.hasStockAtLocation(
          invId,
          branchId,
          locationId,
          locationName,
        );
      });
    }

    const registerAssets = this.filterAssetsByBranch(
      this.reservableAssets().filter(
        (asset) =>
          !String(asset.id).startsWith('inv-') &&
          !this.isPooledPlaceholderAsset(asset),
      ),
      branchId,
      branchName,
    );

    let filteredRegister = registerAssets;
    if (locationId) {
      filteredRegister = registerAssets.filter((asset) => {
        if (
          asset.locationID != null &&
          String(asset.locationID) === locationId
        ) {
          return true;
        }
        if (
          locationName &&
          asset.location?.trim().toLowerCase() === locationName.toLowerCase()
        ) {
          return true;
        }
        const invId = asset.inventoryItemID;
        return (
          !!invId &&
          this.inventory.hasStockAtLocation(
            invId,
            branchId,
            locationId,
            locationName,
          )
        );
      });
    }

    return this.mergeRegisterAndCatalogAssets(
      filteredRegister,
      filteredCatalog,
    );
  }

  private mergeRegisterAndCatalogAssets(
    register: EquipmentAsset[],
    catalog: EquipmentAsset[],
  ): EquipmentAsset[] {
    const merged = new Map<string, EquipmentAsset>();
    for (const asset of register) {
      merged.set(asset.id, asset);
    }
    for (const asset of catalog) {
      merged.set(asset.id, asset);
    }
    return [...merged.values()];
  }

  private isRegisterAssetBackedByInventory(asset: EquipmentAsset): boolean {
    const inventoryItemID =
      asset.inventoryItemID ?? (Number(asset.id) > 0 ? Number(asset.id) : 0);
    if (!inventoryItemID) {
      return false;
    }
    const item = this.inventory.findItemById(inventoryItemID);
    return !!item && this.inventory.isEligibleForEquipmentRental(item);
  }

  /**
   * Rentable catalog rows from Item Master + Current Stock only.
   * No Asset Register row yet (those come from /EquipmentCheckout).
   */
  private buildCatalogRentableAssets(
    excludeInventoryIds: Set<number>,
    branchId?: string,
    branchName?: string,
  ): EquipmentAsset[] {
    const itemsById = new Map(
      this.inventory.allItems().map((item) => [item.id, item]),
    );
    const assets: EquipmentAsset[] = [];
    const seenInventoryIds = new Set<number>();

    for (const snap of this.inventory.allStockSnapshots()) {
      const availableQty = Math.max(
        0,
        snap.quantity - (snap.reservedQuantity ?? 0),
      );
      if (availableQty <= 0) {
        continue;
      }
      const itemId = snap.inventoryItemId;
      if (!itemId) {
        continue;
      }
      const invNum = Number(itemId);
      if (
        !Number.isFinite(invNum) ||
        invNum <= 0 ||
        excludeInventoryIds.has(invNum)
      ) {
        continue;
      }
      const item = itemsById.get(itemId);
      if (!item || !this.inventory.isEligibleForEquipmentRental(item)) {
        continue;
      }
      if (seenInventoryIds.has(invNum)) {
        continue;
      }
      seenInventoryIds.add(invNum);

      // Prefer the stock bin with the most free qty for location display.
      let bestSnap = snap;
      let bestFree = availableQty;
      for (const other of this.inventory.allStockSnapshots()) {
        if (String(other.inventoryItemId ?? '') !== String(itemId)) {
          continue;
        }
        if (
          branchId &&
          branchId !== 'all' &&
          other.branchId &&
          other.branchId !== branchId
        ) {
          continue;
        }
        const free = Math.max(
          0,
          other.quantity - (other.reservedQuantity ?? 0),
        );
        if (free > bestFree) {
          bestFree = free;
          bestSnap = other;
        }
      }

      const asset = this.catalogAssetFromInventoryItem({
        ...item,
        branchId: bestSnap.branchId || item.branchId,
        branch: bestSnap.branch || item.branch,
        location: bestSnap.location || item.location,
      });
      if (bestSnap.locationId) {
        asset.locationID = Number(bestSnap.locationId) || undefined;
      }
      if (this.isAssetReservableForReservation(asset)) {
        assets.push(asset);
      }
    }

    // Include rentable Item Master rows even when no stock balance exists yet.
    for (const item of this.inventory.rentableInventoryItems()) {
      const invNum = Number(item.id);
      if (!Number.isFinite(invNum) || invNum <= 0) {
        continue;
      }
      if (excludeInventoryIds.has(invNum) || seenInventoryIds.has(invNum)) {
        continue;
      }
      seenInventoryIds.add(invNum);
      const asset = this.catalogAssetFromInventoryItem(item);
      if (this.isAssetReservableForReservation(asset)) {
        assets.push(asset);
      }
    }

    return this.filterAssetsByBranch(assets, branchId, branchName);
  }

  private filterAssetsByBranch(
    assets: EquipmentAsset[],
    branchId?: string,
    branchName?: string,
  ): EquipmentAsset[] {
    const branch = branchId?.trim();
    if (!branch || branch === 'all') {
      return assets;
    }

    const branches = this.settings.scopedBranches();
    const branchLabel = branchName?.trim();

    return assets.filter((asset) => {
      const assetBranchId =
        asset.branchID != null ? String(asset.branchID) : '';
      const assetBranchName = (asset.branchName ?? asset.location ?? '').trim();

      if (
        matchesBranchFilter(
          branch,
          assetBranchName,
          assetBranchId || undefined,
          branches,
        )
      ) {
        return true;
      }

      if (
        branchLabel &&
        assetBranchName &&
        assetBranchName.toLowerCase() === branchLabel.toLowerCase()
      ) {
        return true;
      }

      // Catalog / master rows without branch assignment are visible at any branch.
      return !assetBranchId && !assetBranchName;
    });
  }

  private isInventoryItemRentableForRental(item: {
    isRentable?: boolean;
    status: string;
  }): boolean {
    return this.inventory.isEligibleForEquipmentRental(item);
  }

  /**
   * Synthetic Asset Register rows created by
   * usp_IMS_EnsureAssetRegisterForRentableItem (AST-{itemId}-B{branchId}).
   * These are not unique tagged units — stock is reserved by quantity.
   */
  isPooledPlaceholderAsset(asset: {
    id?: string | number;
    assetId?: string;
    inventoryItemID?: number;
  }): boolean {
    const invId = Number(asset.inventoryItemID) || 0;
    const tag = (asset.assetId ?? '').trim().toUpperCase();
    if (/^AST-\d+-B\d+$/.test(tag)) {
      return true;
    }
    if (invId > 0 && tag === `AST-${invId}`) {
      return true;
    }
    return false;
  }

  /** Rentable Item Master stock — reserve by quantity, not one serial at a time. */
  isQuantityBasedEquipment(
    asset?: {
      id?: string | number;
      assetId?: string;
      inventoryItemID?: number;
    } | null,
  ): boolean {
    if (!asset) {
      return false;
    }
    if (String(asset.id ?? '').startsWith('inv-')) {
      return true;
    }
    if (this.isPooledPlaceholderAsset(asset)) {
      return true;
    }
    const invId = Number(asset.inventoryItemID) || 0;
    if (invId <= 0) {
      return false;
    }
    const item = this.inventory.findItemById(invId);
    return !!item && this.inventory.isEligibleForEquipmentRental(item);
  }

  private catalogAssetFromInventoryItem(item: {
    id: string;
    code: string;
    name: string;
    category: string;
    branch?: string;
    branchId?: string;
    location?: string;
    isRentable?: boolean;
  }): EquipmentAsset {
    return {
      id: `inv-${item.id}`,
      assetId: item.code || `ITM-${item.id}`,
      name: item.name,
      serialNumber: item.code,
      category: item.category || 'Equipment',
      location: item.location || item.branch || '',
      status: 'available',
      condition: 'good',
      inventoryItemID: Number(item.id) || 0,
      branchID: Number(item.branchId) || undefined,
      branchName: item.branch,
      isRentable: item.isRentable !== false,
      canCheckout: true,
    };
  }

  /** Reload rentable asset register rows for reservation/check-out forms. */
  refreshEquipmentAssetsFromApi(): Observable<void> {
    if (environment.useMockData) {
      return of(undefined);
    }

    const branchParams = branchQueryParams(this.auth);
    return forkJoin([
      this.inventory.loadItemMasterAndStock(),
      this.api
        .get<GenericMultiResultDto>('/EquipmentCheckout', branchParams)
        .pipe(
          catchError((error) => {
            console.error('Failed to load equipment assets.', error);
            return of(null);
          }),
        ),
    ]).pipe(
      tap(([, checkoutPayload]) => {
        if (checkoutPayload) {
          this.applyCheckouts(checkoutPayload);
        }
      }),
      map(() => undefined),
    );
  }

  /** Resolve EquipmentAssetID for a reservation (matches by name/tag when API omits id). */
  resolveReservationEquipmentAssetId(reservation: Reservation): number {
    const enriched = this.enrichReservationWithAssetId(reservation);
    if (enriched.equipmentAssetID && enriched.equipmentAssetID > 0) {
      return enriched.equipmentAssetID;
    }

    // Quantity catalog rentals must stay asset-less — never bind the shared
    // AST-{item}-B{branch} placeholder or another unit with the same item name.
    if (this.isQuantityBasedReservation(enriched)) {
      return 0;
    }

    return this.resolveEquipmentAssetId(enriched.equipmentAssetID, {
      equipmentName: enriched.equipment,
      assetTag: enriched.reservationNumber,
      inventoryItemID: enriched.inventoryItemID,
    });
  }

  /** InventoryItemID for issuing a reservation (quantity / catalog lines). */
  resolveReservationInventoryItemId(reservation: Reservation): number {
    const enriched = this.enrichReservationWithAssetId(reservation);
    return this.resolveInventoryItemIdForReservation(enriched);
  }

  /** Resolve numeric register id for an equipment row shown in the UI. */
  resolveAssetEquipmentId(asset: EquipmentAsset): number {
    return this.resolveEquipmentAssetId(asset.id, {
      assetTag: asset.assetId,
      equipmentName: asset.name,
      inventoryItemID: asset.inventoryItemID,
    });
  }

  /** Walk-in rentals — free stock only (on hand − reserved), not reservation queue. */
  readonly checkoutDisplayAssets = computed(() => {
    const branchId = defaultBranchFilterId(
      this.auth,
      this.settings.scopedBranches(),
    );
    const branch = this.settings
      .scopedBranches()
      .find((row) => row.id === branchId);
    const scopedBranchId =
      branchId && branchId !== 'all' ? branchId : undefined;

    // Tagged serial units only — pooled AST-{item}-B{branch} placeholders are not walk-in rows.
    const registerAvailable = this.filterAssetsByBranch(
      this.rentableAssets().filter(
        (asset) =>
          !this.isPooledPlaceholderAsset(asset) &&
          !this.isQuantityBasedEquipment(asset) &&
          this.isAssetAvailableForWalkInCheckout(asset),
      ),
      scopedBranchId,
      branch?.name,
    );

    // Only treat unique tagged register rows as "already listed" — never hide catalog
    // stock just because a placeholder Asset Register row exists for the same item.
    const coveredByTaggedUnits = new Set(
      this.rentableAssets()
        .filter(
          (asset) =>
            !this.isPooledPlaceholderAsset(asset) &&
            !this.isQuantityBasedEquipment(asset),
        )
        .map((asset) => asset.inventoryItemID)
        .filter((id): id is number => !!id && id > 0),
    );

    const catalogAvailable = this.buildCatalogRentableAssets(
      coveredByTaggedUnits,
      scopedBranchId,
      branch?.name,
    ).filter((asset) => this.isAssetAvailableForWalkInCheckout(asset));

    return this.mergeRegisterAndCatalogAssets(
      registerAvailable,
      catalogAvailable,
    );
  });

  /** Physically checked-out assets (not paid-waiting). */
  readonly checkedOutAssets = computed(() =>
    this.rentableAssets().filter((asset) => asset.status === 'checked-out'),
  );

  /** Active rental checkouts (includes overdue). */
  readonly activeCheckedOutCheckouts = computed(() =>
    this.checkouts().filter(
      (record) => record.status === 'active' || record.status === 'overdue',
    ),
  );

  /** KPIs for the Check-Out page (excludes damaged, lost, in-repair, checked-out). */
  readonly checkoutPageStats = computed(() => {
    const available = this.checkoutDisplayAssets().length;
    const awaitingCheckout = this.completedReservationsForCheckout().length;
    const activeCheckouts = this.activeCheckedOutCheckouts();
    return {
      available,
      awaitingCheckout,
      readyTotal: available + awaitingCheckout,
      checkedOut: activeCheckouts.length,
      overdue: activeCheckouts.filter((row) => row.status === 'overdue').length,
    };
  });

  readonly stats = computed<EquipmentStats>(() => {
    const list = this.assets();
    const checkouts = this.checkouts();
    const today = new Date().toISOString().slice(0, 10);

    return {
      available: list.filter(
        (asset) =>
          asset.status === 'available' || asset.status === 'awaiting-checkout',
      ).length,
      checkedOut: list.filter((asset) => asset.status === 'checked-out').length,
      dueToday: checkouts.filter(
        (record) =>
          (record.status === 'active' || record.status === 'overdue') &&
          record.dueDate === today,
      ).length,
      overdue: checkouts.filter((record) => record.status === 'overdue').length,
    };
  });

  constructor() {
    if (!environment.useMockData && sessionStorage.getItem('ims-auth')) {
      this.refreshFromApi();
    }
  }

  refreshFromApi(): void {
    if (environment.useMockData) {
      return;
    }

    this.inventory.loadItemMasterAndStock().subscribe(() => {
      this.refreshReservationsFromApi();

      this.api
        .get<GenericMultiResultDto>('/EquipmentCheckout')
        .pipe(
          tap((payload) => this.applyCheckouts(payload)),
          catchError(() => of(null)),
        )
        .subscribe();

      this.refreshActiveCheckoutsFromApi().subscribe();

      this.api
        .get<GenericMultiResultDto>('/Damaged')
        .pipe(
          tap((payload) => this.applyDamaged(payload)),
          catchError(() => of(null)),
        )
        .subscribe();
    });
  }

  /** Reload Check-In / Returns page: active checkouts + assets. */
  refreshCheckInFromApi(): Observable<void> {
    if (environment.useMockData) {
      return of(undefined);
    }

    return forkJoin([
      this.api.get<GenericMultiResultDto>('/EquipmentCheckout').pipe(
        tap((payload) => this.applyCheckouts(payload)),
        catchError(() => of(null)),
      ),
      this.refreshActiveCheckoutsFromApi().pipe(
        catchError(() => of(undefined)),
      ),
    ]).pipe(map(() => undefined));
  }

  /** Reload Overdue page. */
  refreshOverdueFromApi(): Observable<void> {
    if (environment.useMockData) {
      return of(undefined);
    }

    return forkJoin([
      this.api.get<GenericMultiResultDto>('/Overdue').pipe(
        tap((payload) => this.applyOverdue(payload)),
        catchError(() => of(null)),
      ),
      this.refreshActiveCheckoutsFromApi().pipe(
        catchError(() => of(undefined)),
      ),
    ]).pipe(map(() => undefined));
  }

  /** GET /EquipmentCheckout/active — physical rentals (includes quantity-only lines). */
  refreshActiveCheckoutsFromApi(): Observable<void> {
    if (environment.useMockData) {
      return of(undefined);
    }

    const branchParams = branchQueryParams(this.auth);

    return this.api
      .get<{ data?: unknown[] }>('/EquipmentCheckout/active', branchParams)
      .pipe(
        tap((payload) => {
          const rows = Array.isArray(payload?.data) ? payload.data : [];
          this.applyActiveCheckoutRows(rows);
        }),
        map(() => undefined),
        catchError((error) => {
          const detail =
            error?.error?.error ??
            error?.error?.message ??
            error?.message ??
            'Unknown error';
          console.error('Failed to load active checkouts.', detail, error);
          return of(undefined);
        }),
      );
  }

  /** Merge physical active checkout rows from GET /EquipmentCheckout/active. */
  private applyActiveCheckoutRows(rows: unknown[]): void {
    if (!rows.length) {
      // Keep existing physical checkouts; do not wipe on empty/error payloads.
      return;
    }

    const mapped = this.mapCheckoutRows(rows).filter(
      (row) =>
        (row.status === 'active' || row.status === 'overdue') &&
        (row.quantityOutstanding ?? row.quantity ?? 0) > 0,
    );
    // Active-checkouts payload is authoritative — replace overlapping ids and
    // drop ghost rows that the API no longer returns (already fully returned).
    const map = new Map<string, CheckoutRecord>();
    for (const row of mapped) {
      map.set(row.id, row);
    }
    const activeIds = new Set(mapped.map((row) => row.id));
    for (const row of this.checkouts()) {
      if (activeIds.has(row.id)) {
        continue;
      }
      // Keep non-active history rows (returned/cancelled); do not keep stale actives.
      if (row.status !== 'active' && row.status !== 'overdue') {
        map.set(row.id, row);
      }
    }
    this.checkouts.set(
      filterRowsByUserBranch(
        this.auth,
        [...map.values()],
        (row) => row.branchId,
      ),
    );

    // Sync issued reservation flags from persisted active rentals.
    const issued = new Set(this.issuedReservationIds());
    for (const row of mapped) {
      if (row.reservationId) {
        issued.add(row.reservationId);
      }
    }
    this.issuedReservationIds.set(issued);
  }

  refreshReservationsFromApi(): void {
    if (environment.useMockData) {
      return;
    }

    this.api
      .get<GenericMultiResultDto>('/Reservations', branchQueryParams(this.auth))
      .pipe(
        tap((payload) => this.applyReservations(payload)),
        catchError((error) => {
          console.error('Failed to load reservations.', error);
          return of(null);
        }),
      )
      .subscribe();
  }

  /** Reload Check-Out page: assets, reservations, and paid-waiting queue. */
  refreshCheckoutQueueFromApi(): Observable<void> {
    if (environment.useMockData) {
      return of(undefined);
    }

    const branchParams = branchQueryParams(this.auth);

    return this.inventory.refreshInventoryAfterStockMove().pipe(
      switchMap(() =>
        this.api.get<GenericMultiResultDto>('/Reservations', branchParams).pipe(
          tap((payload) =>
            this.applyReservations(payload, { skipQueueRebuild: true }),
          ),
          catchError(() => of(null)),
          switchMap(() =>
            forkJoin([
              this.api
                .get<GenericMultiResultDto>('/EquipmentCheckout', branchParams)
                .pipe(
                  tap((payload) =>
                    this.applyCheckouts(payload, { skipQueueRebuild: true }),
                  ),
                  catchError(() => of(null)),
                ),
              this.api
                .get<GenericMultiResultDto>('/Overdue', branchParams)
                .pipe(
                  tap((payload) => this.applyOverdue(payload)),
                  catchError(() => of(null)),
                ),
              this.api
                .get<{
                  data?: unknown[];
                }>('/EquipmentCheckout/paid-waiting', branchParams)
                .pipe(
                  tap((payload) => {
                    const rows = Array.isArray(payload?.data)
                      ? payload.data
                      : [];
                    this.applyPaidWaitingQueueRows(rows);
                  }),
                  catchError(() => of(null)),
                ),
              this.refreshActiveCheckoutsFromApi().pipe(
                catchError(() => of(undefined)),
              ),
            ]),
          ),
        ),
      ),
      map(() => undefined),
    );
  }

  /**
   * Keep Current Stock, Item Master, Asset Register (via checkout payload),
   * Reservations, and Issue Equipment queues in sync after any rental move.
   */
  syncAfterRentalStockChange(): Observable<void> {
    return this.refreshCheckoutQueueFromApi();
  }

  /** @deprecated Use refreshCheckoutQueueFromApi — queue is computed from reservations. */
  refreshPaidWaitingQueueFromApi(): Observable<void> {
    return this.refreshCheckoutQueueFromApi();
  }

  /** POST /EquipmentReturns */
  processReturnViaApi(
    checkoutId: string,
    details?: {
      returnedByName?: string;
      returnCondition?: string;
      returnNotes?: string;
      returnDate?: string;
      equipmentAssetID?: number;
      quantityReturned?: number;
      quantityDamaged?: number;
      quantityMissing?: number;
      damageDescription?: string;
      repairRequired?: boolean;
      storeLocationID?: number;
      branchID?: number;
    },
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.returnEquipment(checkoutId);
      return of({ success: true });
    }

    const checkout = this.checkouts().find((row) => row.id === checkoutId);
    const equipmentAssetID =
      details?.equipmentAssetID ||
      checkout?.equipmentAssetID ||
      this.resolveAssetIdForCheckout(checkout) ||
      0;

    if (!Number(checkoutId)) {
      return of({
        success: false,
        message: 'Invalid checkout id. Refresh and try again.',
      });
    }

    const returnedByName = (
      details?.returnedByName ||
      checkout?.customer ||
      ''
    ).trim();
    if (!returnedByName) {
      return of({
        success: false,
        message: 'Returned by name is required.',
      });
    }

    const condition = (details?.returnCondition ?? 'Good').trim();
    const routing = this.mapReturnCondition(condition);
    const quantityReturned =
      details?.quantityReturned ?? routing.quantityReturned;
    const quantityDamaged = details?.quantityDamaged ?? routing.quantityDamaged;
    const quantityMissing = details?.quantityMissing ?? routing.quantityMissing;
    // Asset damage/lost report: condition routing, or full write-off with no physical return.
    const damageType: 'damaged' | 'lost' | undefined =
      routing.damageType ??
      (quantityReturned === 0 && quantityMissing > 0
        ? details?.damageDescription?.toLowerCase().includes('damag')
          ? 'damaged'
          : 'lost'
        : undefined);

    return this.api
      .post('/EquipmentReturns', {
        equipmentCheckoutID: Number(checkoutId),
        returnDate: details?.returnDate || new Date().toISOString(),
        returnedByUserID: this.auth.currentUserId(),
        returnedByName,
        returnedByPhone: null,
        storeLocationID:
          details?.storeLocationID && details.storeLocationID > 0
            ? details.storeLocationID
            : null,
        branchID:
          details?.branchID && details.branchID > 0 ? details.branchID : null,
        lines: [
          {
            equipmentAssetID,
            quantityReturned,
            quantityDamaged,
            quantityMissing,
            damageDescription:
              details?.damageDescription ??
              routing.damageDescription ??
              (damageType ? checkout?.equipment : null),
            repairRequired: details?.repairRequired ?? routing.repairRequired,
            conditionIn: condition,
            chargeCustomer: false,
            chargeAmount: 0,
          },
        ],
      })
      .pipe(
        switchMap((payload) => {
          const failed = this.isReturnFailed(payload);
          if (failed) {
            return of({
              success: false,
              message: failed,
            });
          }

          // Return line already records damaged/missing — do not create a duplicate /Damaged row.
          const followUp = of({ success: true });

          return followUp.pipe(
            switchMap((result) => {
              this.returnEquipment(checkoutId);
              this.refreshFromApi();
              return this.syncAfterRentalStockChange().pipe(
                map(() =>
                  result.success
                    ? ({ success: true } satisfies EquipmentApiResult)
                    : ({
                        success: true,
                        message:
                          'Return saved but damage/lost report could not be created.',
                      } satisfies EquipmentApiResult),
                ),
              );
            }),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to process equipment return.')),
        ),
      );
  }

  /** Map return condition to quantities and downstream damaged/lost routing. */
  mapReturnCondition(condition: string): {
    quantityReturned: number;
    quantityDamaged: number;
    quantityMissing: number;
    repairRequired: boolean;
    damageType?: 'damaged' | 'lost';
    damageDescription?: string;
  } {
    const normalized = condition.trim().toLowerCase();

    if (normalized.includes('lost') || normalized.includes('missing')) {
      return {
        quantityReturned: 0,
        quantityDamaged: 0,
        quantityMissing: 1,
        repairRequired: false,
        damageType: 'lost',
        damageDescription: 'Reported lost on return',
      };
    }

    if (
      normalized.includes('damage') ||
      normalized.includes('poor') ||
      normalized.includes('missing parts')
    ) {
      return {
        quantityReturned: 1,
        quantityDamaged: 1,
        quantityMissing: 0,
        repairRequired: true,
        damageType: 'damaged',
        damageDescription: `Returned as ${condition}`,
      };
    }

    return {
      quantityReturned: 1,
      quantityDamaged: 0,
      quantityMissing: 0,
      repairRequired: false,
    };
  }

  private isReturnFailed(payload: unknown): string | null {
    const root = asDict(payload);
    if (root['success'] === false || root['Success'] === false) {
      return pickString(root, 'message', 'Message') || 'Return failed.';
    }

    const data = asDict(root['data'] ?? root['Data'] ?? {});
    const summary = asDict(data['summary'] ?? data['Summary'] ?? root);
    const successRaw = pick(summary, 'Success', 'success');
    if (
      successRaw === false ||
      successRaw === 0 ||
      String(successRaw ?? '').toLowerCase() === 'false'
    ) {
      return pickString(summary, 'Message', 'message') || 'Return failed.';
    }

    return null;
  }

  /** POST /Reservations/create-simple */
  createReservationViaApi(
    reservation: Omit<Reservation, 'id' | 'reservationNumber'> & {
      inventoryItemID?: number;
      equipmentAssetID?: number;
      branchID?: number;
      storeLocationID?: number;
      quantityRequested?: number;
    },
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.addReservation(reservation);
      return of({ success: true });
    }

    let inventoryItemID = Number(reservation.inventoryItemID) || 0;
    inventoryItemID =
      inventoryItemID || this.resolveInventoryItemIdForReservation(reservation);
    const branchID = resolveBranchId(
      this.auth,
      reservation.branchID ?? reservation.branch,
    );
    const requestedAssetId = Number(reservation.equipmentAssetID) || 0;
    // Catalog / quantity reservations send 0. Do not promote them onto the
    // placeholder AST-{item}-B{branch} register row — that used to make qty > 1 fail.
    let equipmentAssetID =
      requestedAssetId > 0
        ? this.resolveEquipmentAssetIdForReservation(
            requestedAssetId,
            inventoryItemID,
            branchID,
          )
        : 0;

    if (inventoryItemID <= 0) {
      return of({
        success: false,
        message: 'Select rentable equipment from the list, then try again.',
      });
    }

    const quantityRequested = Math.max(
      1,
      Math.floor(Number(reservation.quantityRequested) || 1),
    );
    if (quantityRequested < 1) {
      return of({ success: false, message: 'Quantity must be at least 1.' });
    }

    if (equipmentAssetID <= 0) {
      const item = this.inventory
        .allItems()
        .find((row) => Number(row.id) === inventoryItemID);
      if (!item?.isRentable) {
        return of({
          success: false,
          message: 'Selected item is not marked as rentable in Item Master.',
        });
      }
    } else {
      const asset = this.assets().find(
        (row) => Number(row.id) === equipmentAssetID,
      );
      if (!inventoryItemID && asset?.inventoryItemID) {
        inventoryItemID = Number(asset.inventoryItemID);
      }
    }

    if (branchID <= 0) {
      return of({ success: false, message: 'Select a valid branch.' });
    }

    if (!reservation.startDate || !reservation.endDate) {
      return of({
        success: false,
        message: 'Start and end dates are required.',
      });
    }

    const requestedByUserID = this.auth.currentUserId();
    if (requestedByUserID <= 0) {
      return of({
        success: false,
        message:
          'Your user session has no user id. Sign out and sign in again.',
      });
    }

    const statusMap: Record<Reservation['status'], string> = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      completed: 'Completed',
      issued: 'Issued',
      cancelled: 'Cancelled',
    };
    const requestedStatus = statusMap[reservation.status] ?? 'Pending';
    // Create as Pending first; Complete is applied via update so paid-waiting checkout is created.
    const createStatus =
      requestedStatus === 'Completed' ? 'Pending' : requestedStatus;

    return this.api
      .post<unknown>('/Reservations/create-simple', {
        funeralCaseID: null,
        inventoryItemID,
        equipmentAssetID: equipmentAssetID > 0 ? equipmentAssetID : 0,
        branchID,
        storeLocationID:
          Number(reservation.storeLocationID ?? reservation.locationId) > 0
            ? Number(reservation.storeLocationID ?? reservation.locationId)
            : null,
        requiredFromDate: reservation.startDate,
        requiredToDate: reservation.endDate,
        requestedByUserID,
        reservationStatus: createStatus,
        quantityRequested,
        notes: reservation.customer,
      })
      .pipe(
        switchMap((payload) => {
          const created = this.readReservationCreateResult(payload);
          if (!created.success) {
            return of(created);
          }

          if (created.row) {
            this.upsertReservationRow(created.row, {
              customer: reservation.customer,
              branch: reservation.branch,
              branchId: reservation.branchId,
              location: reservation.location,
              locationId: reservation.locationId,
              equipment: reservation.equipment,
              startDate: reservation.startDate,
              endDate: reservation.endDate,
              status: reservation.status,
              quantityRequested: reservation.quantityRequested,
            });
          }

          if (equipmentAssetID <= 0 && inventoryItemID > 0) {
            this.inventory.applyLocalReserveStockEffects({
              branchId: String(branchID),
              storeLocationId: String(
                Number(reservation.storeLocationID ?? reservation.locationId) ||
                  '',
              ),
              locationName: reservation.location,
              lines: [{ inventoryItemID, quantity: quantityRequested }],
            });
          }

          if (requestedStatus !== 'Completed' || !created.reservationId) {
            return this.syncAfterRentalStockChange().pipe(map(() => created));
          }

          return this.updateReservationViaApi(String(created.reservationId), {
            status: 'completed',
            startDate: reservation.startDate,
            endDate: reservation.endDate,
            preserveDisplay: {
              customer: reservation.customer,
              equipment: reservation.equipment,
              branch: reservation.branch,
              branchId: reservation.branchId,
              location: reservation.location,
              locationId: reservation.locationId,
              quantityRequested: reservation.quantityRequested,
              inventoryItemID: reservation.inventoryItemID,
              equipmentAssetID: reservation.equipmentAssetID,
            },
          }).pipe(
            switchMap((result) =>
              result.success
                ? this.syncAfterRentalStockChange().pipe(map(() => result))
                : of(result),
            ),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to create reservation.')),
        ),
      );
  }

  private readReservationCreateResult(payload: unknown): EquipmentApiResult & {
    reservationId?: number;
    row?: unknown;
  } {
    const root = asDict(payload);
    const summary = asDict(root['summary'] ?? root['Summary'] ?? {});
    const sets = (pick<unknown[]>(root, 'resultSets', 'ResultSets') ??
      []) as unknown[];
    const firstSet = Array.isArray(sets[0]) ? (sets[0] as unknown[]) : [];
    const firstRow = asDict(firstSet[0] ?? {});
    const source = Object.keys(summary).length ? summary : firstRow;

    const successRaw = pick(source, 'Success', 'success');
    const failed =
      successRaw === false ||
      successRaw === 0 ||
      String(successRaw ?? '').toLowerCase() === 'false';
    const message = pickString(source, 'Message', 'message');
    const reservationId = pickNumber(
      source,
      'EquipmentReservationID',
      'equipmentReservationID',
      'ReservationId',
    );

    if (failed) {
      return {
        success: false,
        message: message || 'Failed to create reservation.',
      };
    }

    return {
      success: true,
      message: message || undefined,
      reservationId: reservationId > 0 ? reservationId : undefined,
      row: Object.keys(source).length ? source : undefined,
    };
  }

  /** POST /Damaged */
  createDamageReportViaApi(
    record: Omit<DamagedEquipmentRecord, 'id' | 'reportNumber'> & {
      equipmentAssetID?: number;
    },
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.addDamagedRecord(record);
      return of({ success: true });
    }

    const equipmentAssetID = record.equipmentAssetID ?? 0;
    if (equipmentAssetID <= 0) {
      return of({
        success: false,
        message: 'A valid equipment asset is required to report damage.',
      });
    }

    return this.api
      .post('/Damaged', {
        equipmentAssetID,
        quantityDamaged: record.damageType === 'damaged' ? 1 : 0,
        quantityMissing: record.damageType === 'lost' ? 1 : 0,
        repairRequired: record.damageType === 'damaged',
        chargeCustomer: false,
        chargeAmount: record.estimatedCost,
        damageDescription: record.equipment,
        reportedByUserID: this.auth.currentUserId(),
        reportType: record.damageType === 'lost' ? 'LOST' : 'DAMAGED',
      })
      .pipe(
        tap(() => this.refreshFromApi()),
        map(() => ({ success: true })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to create damage report.')),
        ),
      );
  }

  /** POST /EquipmentCheckout */
  createCheckoutViaApi(
    assetId: string,
    customer: string,
    dueDate: string,
    contact?: {
      phone?: string;
      vehicleRegistration?: string;
      reservationId?: string;
      destination?: string;
    },
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.checkoutEquipment(assetId, customer, dueDate);
      return of({ success: true });
    }

    const displayAsset =
      this.checkoutDisplayAssets().find((row) => row.id === assetId) ??
      this.assets().find((row) => row.id === assetId);

    if (displayAsset?.pendingCheckoutId) {
      return this.releaseReservationCheckoutViaApi(
        displayAsset,
        customer,
        dueDate,
        contact,
      );
    }

    const asset =
      displayAsset ?? this.assets().find((row) => row.id === assetId);
    let equipmentAssetID = this.resolveEquipmentAssetId(assetId, {
      assetTag: asset?.assetId,
      equipmentName: asset?.name,
      inventoryItemID: asset?.inventoryItemID,
    });
    const inventoryItemID =
      asset?.inventoryItemID ??
      (String(assetId).startsWith('inv-') ? Number(assetId.slice(4)) || 0 : 0);
    const branchID = resolveBranchId(this.auth, asset?.branchID);
    const releasedByUserID = this.auth.currentUserId();
    const reservationId = Number(
      contact?.reservationId ?? asset?.reservationId ?? 0,
    );

    if (inventoryItemID <= 0 && equipmentAssetID <= 0) {
      return of({
        success: false,
        message:
          'Could not resolve this equipment. Refresh the page and select the item from the walk-in list.',
      });
    }

    if (equipmentAssetID > 0 && String(assetId).startsWith('inv-')) {
      equipmentAssetID = 0;
    }

    if (!customer.trim()) {
      return of({
        success: false,
        message: 'Customer / case name is required.',
      });
    }

    if (!(contact?.destination ?? '').trim()) {
      return of({
        success: false,
        message: 'Destination (where the equipment is going) is required.',
      });
    }

    if (!dueDate) {
      return of({
        success: false,
        message: 'Expected return date is required.',
      });
    }

    if (branchID <= 0) {
      return of({
        success: false,
        message:
          'Could not resolve branch for this checkout. Log out and sign in again.',
      });
    }

    const pendingCheckoutId = this.resolveCheckoutIdForRelease(
      String(equipmentAssetID),
      contact?.reservationId ?? asset?.reservationId,
    );
    if (pendingCheckoutId) {
      return this.releaseReservationCheckoutViaApi(
        {
          ...(asset ?? {
            id: String(equipmentAssetID),
            assetId: `AST-${equipmentAssetID}`,
            name: 'Equipment',
            serialNumber: '',
            category: 'Equipment',
            location: '',
            condition: 'good' as const,
          }),
          pendingCheckoutId,
          status:
            asset?.status === 'checked-out'
              ? 'awaiting-checkout'
              : (asset?.status ?? 'awaiting-checkout'),
          reservationId: contact?.reservationId ?? asset?.reservationId,
        },
        customer,
        dueDate,
        contact,
      );
    }

    if (releasedByUserID <= 0) {
      return of({
        success: false,
        message:
          'Could not resolve your user account. Log out and sign in again.',
      });
    }

    const isWalkIn = !(
      contact?.reservationId ||
      asset?.reservationId ||
      asset?.pendingCheckoutId
    );
    if (isWalkIn && inventoryItemID > 0 && equipmentAssetID <= 0) {
      const atLocation = this.inventory.availableQuantityAtLocation(
        inventoryItemID,
        String(branchID),
        undefined,
        asset?.location,
      );
      const available =
        atLocation > 0
          ? atLocation
          : this.inventory.availableQuantityAtBranch(
              inventoryItemID,
              String(branchID),
            );
      const check = assertCanTake(available, 1, asset?.name || 'this item');
      if (!check.ok) {
        return of({ success: false, message: check.message });
      }
    }
    if (isWalkIn && equipmentAssetID > 0) {
      const status = asset?.status;
      if (status && status !== 'available' && status !== 'awaiting-checkout') {
        return of({
          success: false,
          message: `${asset?.name || 'This asset'} is not available to check out.`,
        });
      }
    }

    if (this.isAssetAlreadyCheckedOut(String(equipmentAssetID))) {
      const checkoutId =
        this.resolveActiveCheckoutIdForAsset(String(equipmentAssetID)) ??
        this.resolveCheckoutIdForRelease(
          String(equipmentAssetID),
          contact?.reservationId ?? asset?.reservationId,
        );
      if (checkoutId) {
        return this.putReleaseCheckoutViaApi(checkoutId, customer, dueDate, {
          ...contact,
          assetId: String(equipmentAssetID),
          reservationId: contact?.reservationId ?? asset?.reservationId,
        });
      }

      return this.refreshCheckoutQueueFromApi().pipe(
        switchMap(() => {
          const retryId = this.resolveCheckoutIdForRelease(
            String(equipmentAssetID),
            contact?.reservationId ?? asset?.reservationId,
          );
          if (retryId) {
            return this.putReleaseCheckoutViaApi(retryId, customer, dueDate, {
              ...contact,
              assetId: String(equipmentAssetID),
              reservationId: contact?.reservationId ?? asset?.reservationId,
            });
          }
          const stillCheckedOut = this.isAssetAlreadyCheckedOut(
            String(equipmentAssetID),
          );
          return of({
            success: stillCheckedOut,
            message: stillCheckedOut
              ? 'This equipment is already checked out. See Check-In for active rentals.'
              : 'Could not confirm checkout status. Please try again.',
          } satisfies EquipmentApiResult);
        }),
      );
    }

    return this.api
      .post('/EquipmentCheckout', {
        equipmentReservationID: reservationId > 0 ? reservationId : null,
        funeralCaseID: null,
        branchID,
        checkoutDate: new Date().toISOString(),
        expectedReturnDate: dueDate,
        releasedByUserID,
        receivedByName: customer,
        receivedByPhone: contact?.phone?.trim() || null,
        vehicleRegistration: contact?.vehicleRegistration?.trim() || null,
        notes: null,
        destinationLocation: contact?.destination?.trim() || null,
        lines: [
          {
            inventoryItemID: inventoryItemID > 0 ? inventoryItemID : null,
            equipmentAssetID: equipmentAssetID > 0 ? equipmentAssetID : null,
            quantity: 1,
          },
        ],
      })
      .pipe(
        switchMap(() => {
          const reservationKey = contact?.reservationId ?? asset?.reservationId;
          if (reservationKey) {
            this.markReservationIssued(reservationKey, {
              customer,
              dueDate,
              assetId: String(equipmentAssetID > 0 ? equipmentAssetID : ''),
            });
          }

          return this.refreshCheckoutQueueFromApi().pipe(
            map(() => {
              let verifyAssetId = String(equipmentAssetID);
              if (Number(equipmentAssetID) <= 0 && inventoryItemID > 0) {
                const resolved = this.assets().find(
                  (row) =>
                    row.inventoryItemID === inventoryItemID &&
                    !String(row.id).startsWith('inv-'),
                );
                if (resolved) {
                  verifyAssetId = resolved.id;
                }
              }
              const persisted = this.verifyPhysicalCheckoutPersisted(
                verifyAssetId,
                reservationKey,
              );
              if (
                persisted ||
                (reservationKey &&
                  this.issuedReservationIds().has(reservationKey))
              ) {
                return { success: true } satisfies EquipmentApiResult;
              }
              return {
                success: false,
                message:
                  'Checkout was not saved in the database. Restart the gateway and try again.',
              } satisfies EquipmentApiResult;
            }),
          );
        }),
        catchError((error) => {
          if (this.isAlreadyCheckedOutError(error)) {
            const releaseId = this.resolveCheckoutIdForRelease(
              String(equipmentAssetID),
              contact?.reservationId ?? asset?.reservationId,
            );
            if (releaseId) {
              return this.putReleaseCheckoutViaApi(
                releaseId,
                customer,
                dueDate,
                {
                  ...contact,
                  assetId: String(equipmentAssetID),
                  reservationId: contact?.reservationId ?? asset?.reservationId,
                },
              );
            }
            return this.refreshCheckoutQueueFromApi().pipe(
              switchMap(() => {
                const retryId = this.resolveCheckoutIdForRelease(
                  String(equipmentAssetID),
                  contact?.reservationId ?? asset?.reservationId,
                );
                if (retryId) {
                  return this.putReleaseCheckoutViaApi(
                    retryId,
                    customer,
                    dueDate,
                    {
                      ...contact,
                      assetId: String(equipmentAssetID),
                      reservationId:
                        contact?.reservationId ?? asset?.reservationId,
                    },
                  );
                }
                return of({
                  success: this.isAssetAlreadyCheckedOut(
                    String(equipmentAssetID),
                  ),
                  message: this.isAssetAlreadyCheckedOut(
                    String(equipmentAssetID),
                  )
                    ? 'This equipment is already checked out. See Check-In for active rentals.'
                    : 'Could not confirm checkout status. Please try again.',
                } satisfies EquipmentApiResult);
              }),
            );
          }
          return of(this.toApiError(error, 'Failed to check out equipment.'));
        }),
      );
  }

  /**
   * Checkout rentable inventory items from Issue Stock (quantity, no asset tag).
   * POST /EquipmentCheckout — appears on Issue Equipment / Check-In, not Issued Stock.
   */
  checkoutInventoryItemsViaApi(input: {
    branchId: string;
    storeLocationId?: string;
    locationName?: string;
    customer: string;
    phone?: string;
    dueDate: string;
    notes?: string;
    destination?: string;
    vehicleRegistration?: string;
    lines: Array<{
      inventoryItemID: number;
      quantity: number;
      itemName?: string;
    }>;
  }): Observable<EquipmentApiResult> {
    const lines = input.lines.filter(
      (line) => line.inventoryItemID > 0 && line.quantity > 0,
    );
    if (!lines.length) {
      return of({ success: true });
    }

    const customer = input.customer.trim();
    const dueDate = input.dueDate;
    if (!customer) {
      return of({ success: false, message: 'Recipient name is required.' });
    }
    if (!dueDate) {
      return of({
        success: false,
        message: 'Expected return date is required for rentable items.',
      });
    }

    const destination = (input.destination ?? '').trim();
    if (!destination) {
      return of({
        success: false,
        message: 'Destination (where the equipment is going) is required.',
      });
    }

    const storeLocationId = (input.storeLocationId ?? '').trim();
    if (!storeLocationId) {
      return of({
        success: false,
        message: 'Select a store location to take stock from.',
      });
    }

    for (const line of lines) {
      const parsed = parsePositiveInt(
        line.quantity,
        line.itemName || 'Quantity',
      );
      if (!parsed.ok) {
        return of({ success: false, message: parsed.message });
      }
      const available = this.inventory.availableQuantityAtLocation(
        line.inventoryItemID,
        input.branchId,
        storeLocationId,
        input.locationName,
      );
      const check = assertCanTake(
        available,
        parsed.value,
        line.itemName || `Item ${line.inventoryItemID}`,
      );
      if (!check.ok) {
        return of({
          success: false,
          message:
            (check.message ?? `Only ${available} available.`) +
            ' Choose another store location or reduce quantity.',
        });
      }
    }

    if (environment.useMockData) {
      const names = lines
        .map((line) => line.itemName || `Item ${line.inventoryItemID}`)
        .join(', ');
      const next = this.checkouts().length + 1;
      const record: CheckoutRecord = {
        id: crypto.randomUUID(),
        checkoutNumber: `CHK-2026-${String(112 + next).padStart(4, '0')}`,
        customer,
        receiver: customer,
        receiverPhone: input.phone,
        equipment: names,
        checkoutDate: new Date().toISOString().slice(0, 10),
        dueDate,
        status: 'active',
        inventoryItemID: lines[0]?.inventoryItemID,
      };
      this.checkouts.update((list) => [record, ...list]);
      this.inventory.applyLocalCheckoutStockEffects({
        branchId: input.branchId,
        storeLocationId: input.storeLocationId ?? '',
        locationName: input.locationName,
        lines: lines.map((line) => ({
          inventoryItemID: line.inventoryItemID,
          quantity: line.quantity,
        })),
      });
      return of({ success: true });
    }

    const branchID = resolveBranchId(
      this.auth,
      Number(input.branchId) || undefined,
    );
    const releasedByUserID = this.auth.currentUserId();

    if (branchID <= 0) {
      return of({
        success: false,
        message:
          'Could not resolve branch for this checkout. Log out and sign in again.',
      });
    }

    if (releasedByUserID <= 0) {
      return of({
        success: false,
        message:
          'Could not resolve your user account. Log out and sign in again.',
      });
    }

    return this.api
      .post('/EquipmentCheckout', {
        equipmentReservationID: null,
        funeralCaseID: null,
        branchID,
        storeLocationID: Number(input.storeLocationId) || null,
        checkoutDate: new Date().toISOString(),
        expectedReturnDate: dueDate,
        releasedByUserID,
        receivedByName: customer,
        receivedByPhone: input.phone?.trim() || null,
        vehicleRegistration: input.vehicleRegistration?.trim() || null,
        notes: input.notes?.trim() || null,
        destinationLocation: destination,
        // Pooled rentables carry no asset tag, so each line checks out by quantity.
        bulkRental: true,
        lines: lines.map((line) => ({
          inventoryItemID: line.inventoryItemID,
          equipmentAssetID: null,
          quantity: line.quantity,
        })),
      })
      .pipe(
        switchMap(() => {
          this.inventory.applyLocalCheckoutStockEffects({
            branchId: String(branchID),
            storeLocationId: String(Number(input.storeLocationId) || ''),
            locationName: input.locationName,
            lines: lines.map((line) => ({
              inventoryItemID: line.inventoryItemID,
              quantity: line.quantity,
            })),
          });

          return this.inventory.refreshInventoryAfterStockMove().pipe(
            switchMap(() => this.syncAfterRentalStockChange()),
            map(() => ({ success: true }) as EquipmentApiResult),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to check out rentable items.')),
        ),
      );
  }

  /** PUT /Reservations/{id} */
  updateReservationViaApi(
    id: string,
    patch: Partial<Reservation> & {
      notes?: string;
      equipmentAssetID?: number;
      inventoryItemID?: number;
      quantityRequested?: number;
      preserveDisplay?: Partial<Reservation>;
    },
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.updateReservation(id, {
        ...patch,
        ...(patch.preserveDisplay ?? {}),
      });
      return of({ success: true });
    }

    const reservationId = Number(id);
    if (!Number.isFinite(reservationId) || reservationId <= 0) {
      return of({
        success: false,
        message: 'Invalid reservation id. Refresh and try again.',
      });
    }

    const statusMap: Record<Reservation['status'], string> = {
      pending: 'Pending',
      confirmed: 'Confirmed',
      completed: 'Completed',
      issued: 'Issued',
      cancelled: 'Cancelled',
    };

    const statusOnly = !!patch.preserveDisplay;
    const body: Record<string, unknown> = {
      status: statusMap[patch.status ?? 'pending'] ?? 'Pending',
      requiredFromDate: patch.startDate || null,
      requiredToDate: patch.endDate || null,
    };

    if (!statusOnly) {
      const quantityRequested = Math.max(
        0,
        Math.floor(Number(patch.quantityRequested) || 0),
      );
      body['notes'] = patch.notes ?? patch.customer ?? null;
      body['equipmentAssetID'] = patch.equipmentAssetID ?? null;
      body['inventoryItemID'] = patch.inventoryItemID ?? null;
      body['quantityRequested'] =
        quantityRequested > 0 ? quantityRequested : null;
    }

    return this.api.put(`/Reservations/${reservationId}`, body).pipe(
      switchMap(() => {
        if (statusOnly && patch.preserveDisplay) {
          return this.refreshReservationRowPreservingDisplay(
            id,
            patch.preserveDisplay,
            {
              status: patch.status,
              startDate: patch.startDate,
              endDate: patch.endDate,
            },
          );
        }

        return this.api
          .get<GenericMultiResultDto>(
            '/Reservations',
            branchQueryParams(this.auth),
          )
          .pipe(
            tap((payload) =>
              this.applyReservations(payload, { skipQueueRebuild: true }),
            ),
            switchMap(() => this.refreshCheckoutQueueFromApi()),
          );
      }),
      map(() => ({ success: true })),
      catchError((error) =>
        of(this.toApiError(error, 'Failed to update reservation.')),
      ),
    );
  }

  /** Reload reservations from API but keep known-good display fields for one row. */
  private refreshReservationRowPreservingDisplay(
    id: string,
    preserve: Partial<Reservation>,
    updates: Partial<Pick<Reservation, 'status' | 'startDate' | 'endDate'>>,
  ): Observable<void> {
    return this.api
      .get<GenericMultiResultDto>('/Reservations', branchQueryParams(this.auth))
      .pipe(
        tap((payload) =>
          this.applyReservations(payload, { skipQueueRebuild: true }),
        ),
        tap(() => {
          this.reservations.update((list) =>
            list.map((row) =>
              row.id === id
                ? {
                    ...row,
                    ...preserve,
                    ...(updates.status ? { status: updates.status } : {}),
                    ...(updates.startDate
                      ? { startDate: updates.startDate }
                      : {}),
                    ...(updates.endDate ? { endDate: updates.endDate } : {}),
                  }
                : row,
            ),
          );
        }),
        switchMap(() => this.refreshCheckoutQueueFromApi()),
      );
  }

  /** DELETE /Reservations/{id} — soft-cancels */
  deleteReservationViaApi(id: string): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.deleteReservation(id);
      return of({ success: true });
    }

    const reservationId = Number(id);
    if (!Number.isFinite(reservationId) || reservationId <= 0) {
      return of({
        success: false,
        message: 'Invalid reservation id. Refresh and try again.',
      });
    }

    return this.api.delete(`/Reservations/${reservationId}`).pipe(
      switchMap(() => this.syncAfterRentalStockChange()),
      map(() => ({ success: true })),
      catchError((error) =>
        of(this.toApiError(error, 'Failed to cancel reservation.')),
      ),
    );
  }

  /** PUT /EquipmentCheckout/{id} */
  updateCheckoutViaApi(
    id: string,
    patch: {
      customer?: string;
      receiver?: string;
      receiverPhone?: string;
      dueDate?: string;
      returnNotes?: string;
      status?: 'active' | 'returned' | 'overdue' | 'cancelled';
    },
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      const { status, ...localPatch } = patch;
      this.updateCheckout(id, {
        ...localPatch,
        ...(status && status !== 'cancelled' ? { status } : {}),
      });
      if (status === 'cancelled') {
        this.deleteCheckout(id);
      }
      return of({ success: true });
    }

    const checkoutId = Number(id);
    if (!Number.isFinite(checkoutId) || checkoutId <= 0) {
      return of({
        success: false,
        message: 'Invalid checkout id. Refresh and try again.',
      });
    }

    const requestedStatus = patch.status;
    const status =
      requestedStatus === 'cancelled'
        ? 'Cancelled'
        : requestedStatus === 'returned'
          ? 'Returned'
          : 'Checked_Out';

    return this.api
      .put(`/EquipmentCheckout/${checkoutId}`, {
        status,
        receivedByName: patch.customer ?? patch.receiver ?? null,
        receivedByPhone: patch.receiverPhone ?? null,
        expectedReturnDate: patch.dueDate || null,
        notes: patch.returnNotes ?? null,
      })
      .pipe(
        switchMap(() =>
          this.refreshCheckoutQueueFromApi().pipe(
            map(() => ({ success: true as const })),
            catchError((error) =>
              of(this.toApiError(error, 'Failed to update checkout.')),
            ),
          ),
        ),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to update checkout.')),
        ),
      );
  }

  /** Release paid-waiting checkout (reservation completed → physical check-out). */
  releaseReservationCheckoutViaApi(
    asset: EquipmentAsset,
    customer: string,
    dueDate: string,
    contact?: {
      phone?: string;
      vehicleRegistration?: string;
      reservationId?: string;
      destination?: string;
    },
  ): Observable<EquipmentApiResult> {
    const awaitingIssue =
      asset.status === 'awaiting-checkout' ||
      !!asset.pendingCheckoutId ||
      this.isAssetAwaitingPhysicalIssue(asset.id);

    if (!awaitingIssue && this.isAssetAlreadyCheckedOut(asset.id)) {
      const checkoutId = this.resolveActiveCheckoutIdForAsset(asset.id);
      if (checkoutId) {
        return this.updateCheckoutViaApi(checkoutId, {
          customer,
          receiverPhone: contact?.phone?.trim(),
          dueDate,
          status: 'active',
        });
      }

      return this.refreshCheckoutQueueFromApi().pipe(
        map(() => ({
          success: this.isAssetAlreadyCheckedOut(asset.id),
          message:
            'This equipment is already checked out. See Check-In for active rentals.',
        })),
      );
    }

    const checkoutId =
      asset.pendingCheckoutId ??
      this.resolveCheckoutIdForRelease(
        asset.id,
        asset.reservationId ?? contact?.reservationId,
      );

    if (!checkoutId) {
      return this.createCheckoutViaApi(asset.id, customer, dueDate, {
        ...contact,
        reservationId: contact?.reservationId ?? asset.reservationId,
      });
    }

    return this.putReleaseCheckoutViaApi(checkoutId, customer, dueDate, {
      ...contact,
      assetId: asset.id,
      reservationId: contact?.reservationId ?? asset.reservationId,
    }).pipe(
      catchError((error) => {
        if (this.isAlreadyCheckedOutError(error)) {
          return this.updateCheckoutViaApi(checkoutId, {
            customer,
            receiverPhone: contact?.phone?.trim(),
            dueDate,
            status: 'active',
          });
        }
        return of(
          this.toApiError(error, 'Failed to release reserved equipment.'),
        );
      }),
    );
  }

  /** Check out equipment linked to a completed reservation (queue → physical checkout). */
  checkoutFromReservationViaApi(
    reservation: Reservation,
    customer: string,
    dueDate: string,
    contact?: {
      phone?: string;
      vehicleRegistration?: string;
      reservationId?: string;
      destination?: string;
    },
  ): Observable<EquipmentApiResult> {
    const enriched = this.enrichReservationWithAssetId(reservation);
    const equipmentAssetID = this.resolveEquipmentAssetId(
      enriched.equipmentAssetID,
      {
        equipmentName: enriched.equipment,
        assetTag: enriched.reservationNumber,
        inventoryItemID: enriched.inventoryItemID,
      },
    );
    const assetId = equipmentAssetID > 0 ? String(equipmentAssetID) : '';
    const pendingCheckoutId =
      enriched.pendingCheckoutId ??
      this.resolveCheckoutIdForRelease(assetId || undefined, enriched.id);

    // Quantity / catalog reservations often have no AssetRegister row — release by
    // paid-waiting checkout id alone.
    if (pendingCheckoutId) {
      const displayAsset =
        this.checkoutDisplayAssets().find(
          (row) =>
            (assetId && row.id === assetId) ||
            row.reservationId === enriched.id,
        ) ?? this.assets().find((row) => row.reservationId === enriched.id);

      const asset: EquipmentAsset = {
        ...(displayAsset ?? {
          id: assetId || `res-${enriched.id}`,
          assetId: assetId ? `AST-${assetId}` : enriched.reservationNumber,
          name: enriched.equipment,
          serialNumber: '',
          category: 'Equipment',
          location: enriched.location ?? enriched.branch ?? '',
          condition: 'good' as const,
          inventoryItemID: enriched.inventoryItemID,
          isRentable: true,
        }),
        status: 'awaiting-checkout',
        reservationId: enriched.id,
        pendingCheckoutId,
        reservedCustomer: enriched.customer,
        reservedDueDate: enriched.endDate,
      };

      return this.releaseReservationCheckoutViaApi(asset, customer, dueDate, {
        ...contact,
        reservationId: enriched.id,
      });
    }

    if (!assetId) {
      const inventoryItemID = Number(enriched.inventoryItemID) || 0;
      if (inventoryItemID <= 0) {
        return of({
          success: false,
          message:
            'Could not link this reservation to equipment. Refresh, then try again or re-save the reservation.',
        });
      }

      return this.createCheckoutViaApi(
        `inv-${inventoryItemID}`,
        customer,
        dueDate,
        {
          ...contact,
          reservationId: enriched.id,
        },
      );
    }

    if (
      this.isReservationAlreadyCheckedOut(enriched) &&
      !this.isReservationAwaitingPhysicalIssue(enriched)
    ) {
      const checkoutId = this.resolveActiveCheckoutIdForAsset(assetId);
      if (checkoutId) {
        return this.updateCheckoutViaApi(checkoutId, {
          customer,
          receiverPhone: contact?.phone?.trim(),
          dueDate,
        });
      }
      return this.refreshCheckoutQueueFromApi().pipe(
        map(() => ({
          success: true,
          message:
            'This equipment is already checked out. See Check-In for active rentals.',
        })),
      );
    }

    return this.createCheckoutViaApi(assetId, customer, dueDate, {
      ...contact,
      reservationId: enriched.id,
    });
  }

  /** Resolve paid-waiting checkout id for a completed reservation (if the API linked one). */
  resolvePendingCheckoutForReservation(
    reservation: Reservation,
  ): string | undefined {
    return this.resolvePendingCheckoutIdForReservation(reservation);
  }

  private resolvePendingCheckoutIdForReservation(
    reservation: Reservation,
  ): string | undefined {
    if (reservation.pendingCheckoutId) {
      return reservation.pendingCheckoutId;
    }

    const assetId = reservation.equipmentAssetID
      ? String(reservation.equipmentAssetID)
      : '';
    return this.resolvePendingCheckoutIdForAsset(assetId, reservation.id);
  }

  private resolvePendingCheckoutIdForAsset(
    assetId?: string,
    reservationId?: string,
  ): string | undefined {
    if (!assetId && !reservationId) {
      return undefined;
    }

    if (reservationId) {
      const reservation = this.reservations().find(
        (row) => row.id === reservationId,
      );
      if (reservation?.pendingCheckoutId) {
        return reservation.pendingCheckoutId;
      }

      const assetForReservation = this.assets().find(
        (row) => row.reservationId === reservationId && !!row.pendingCheckoutId,
      );
      if (assetForReservation?.pendingCheckoutId) {
        return assetForReservation.pendingCheckoutId;
      }
    }

    // Only fall back to asset when we have a reservation-scoped asset row.
    // Never steal another reservation's pending id just because they share a
    // placeholder AST-{item}-B{branch} asset.
    if (assetId && reservationId) {
      const asset = this.assets().find(
        (row) =>
          row.id === assetId &&
          row.reservationId === reservationId &&
          !!row.pendingCheckoutId,
      );
      if (asset?.pendingCheckoutId) {
        return asset.pendingCheckoutId;
      }
    }

    return undefined;
  }

  /** Checkout row to release when issuing equipment from a paid reservation. */
  private resolveCheckoutIdForRelease(
    assetId?: string,
    reservationId?: string,
  ): string | undefined {
    if (!assetId && !reservationId) {
      return undefined;
    }

    const pending = this.resolvePendingCheckoutIdForAsset(
      assetId,
      reservationId,
    );
    if (pending) {
      return pending;
    }

    const asset = assetId
      ? this.assets().find((row) => row.id === assetId)
      : undefined;
    if (asset?.pendingCheckoutId) {
      return asset.pendingCheckoutId;
    }

    if (
      assetId &&
      asset?.activeCheckoutId &&
      this.isAssetAwaitingPhysicalIssue(assetId)
    ) {
      return asset.activeCheckoutId;
    }

    if (reservationId) {
      const reservation = this.reservations().find(
        (row) => row.id === reservationId,
      );
      if (
        reservation &&
        this.isReservationAwaitingPhysicalIssue(reservation) &&
        asset?.activeCheckoutId
      ) {
        return asset.activeCheckoutId;
      }
    }

    return undefined;
  }

  private putReleaseCheckoutViaApi(
    checkoutId: string,
    customer: string,
    dueDate: string,
    contact?: {
      phone?: string;
      assetId?: string;
      reservationId?: string;
      destination?: string;
      vehicleRegistration?: string;
    },
  ): Observable<EquipmentApiResult> {
    const numericId = Number(checkoutId);
    if (!Number.isFinite(numericId) || numericId <= 0) {
      return of({
        success: false,
        message: 'Invalid checkout id. Refresh and try again.',
      });
    }

    const assetId = contact?.assetId ?? '';
    const reservationId = contact?.reservationId;

    return this.api
      .put(`/EquipmentCheckout/${numericId}`, {
        status: 'Checked_Out',
        receivedByName: customer,
        receivedByPhone: contact?.phone?.trim() || null,
        expectedReturnDate: dueDate,
        destinationLocation: contact?.destination?.trim() || null,
        vehicleRegistration: contact?.vehicleRegistration?.trim() || null,
      })
      .pipe(
        switchMap(() => {
          if (reservationId) {
            this.markReservationIssued(reservationId, {
              checkoutId: String(numericId),
              customer,
              dueDate,
              assetId,
            });
          }

          return this.refreshCheckoutQueueFromApi().pipe(
            map(() => {
              const persisted = this.verifyPhysicalCheckoutPersisted(
                assetId,
                reservationId,
              );
              if (
                persisted ||
                (reservationId &&
                  this.issuedReservationIds().has(reservationId))
              ) {
                return { success: true } satisfies EquipmentApiResult;
              }
              return {
                success: false,
                message:
                  'Checkout was not saved in the database. Deploy usp_UpdateEquipmentCheckout.sql, restart the gateway, then try again.',
              } satisfies EquipmentApiResult;
            }),
          );
        }),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to release reserved equipment.')),
        ),
      );
  }

  /** Mark a reservation as physically issued so the Issue queue updates immediately. */
  private markReservationIssued(
    reservationId: string,
    details?: {
      checkoutId?: string;
      customer?: string;
      dueDate?: string;
      assetId?: string;
    },
  ): void {
    if (!reservationId) {
      return;
    }

    this.issuedReservationIds.update((current) => {
      const next = new Set(current);
      next.add(reservationId);
      return next;
    });

    this.reservations.update((list) =>
      list.map((row) =>
        row.id === reservationId
          ? { ...row, pendingCheckoutId: undefined, status: 'issued' as const }
          : row,
      ),
    );

    if (details?.checkoutId) {
      const reservation = this.reservations().find(
        (row) => row.id === reservationId,
      );
      const existing = this.checkouts().find(
        (row) => row.id === details.checkoutId,
      );
      const nextRow: CheckoutRecord = {
        id: details.checkoutId,
        checkoutNumber: existing?.checkoutNumber ?? `CHK-${details.checkoutId}`,
        customer:
          details.customer ??
          reservation?.customer ??
          existing?.customer ??
          'Customer',
        equipment: reservation?.equipment ?? existing?.equipment ?? 'Equipment',
        branch: reservation?.branch ?? existing?.branch,
        branchId: reservation?.branchId ?? existing?.branchId,
        location: reservation?.location ?? existing?.location,
        reservationNumber:
          reservation?.reservationNumber ?? existing?.reservationNumber,
        checkoutDate:
          existing?.checkoutDate ?? new Date().toISOString().slice(0, 10),
        dueDate:
          details.dueDate ?? reservation?.endDate ?? existing?.dueDate ?? '',
        status: 'active',
        equipmentAssetID:
          (details.assetId && Number(details.assetId)) ||
          reservation?.equipmentAssetID ||
          existing?.equipmentAssetID,
        inventoryItemID:
          reservation?.inventoryItemID ?? existing?.inventoryItemID,
        reservationId,
      };

      this.checkouts.update((list) => {
        const map = new Map(list.map((row) => [row.id, row]));
        map.set(nextRow.id, { ...existing, ...nextRow });
        return [...map.values()];
      });
    }
  }

  /** True when GET /EquipmentCheckout reflects a physical checkout (not paid-waiting). */
  private verifyPhysicalCheckoutPersisted(
    assetId: string,
    reservationId?: string,
  ): boolean {
    if (reservationId && this.issuedReservationIds().has(reservationId)) {
      const reservation = this.reservations().find(
        (row) => row.id === reservationId,
      );
      if (
        reservation &&
        !this.isReservationAwaitingPhysicalIssue(reservation)
      ) {
        return true;
      }
      // Issued locally and no longer paid-waiting in queue.
      if (!reservation?.pendingCheckoutId) {
        return true;
      }
    }

    if (reservationId) {
      const byReservation = this.checkouts().find(
        (row) =>
          row.reservationId === reservationId &&
          (row.status === 'active' || row.status === 'overdue'),
      );
      if (byReservation) {
        return true;
      }

      const reservation = this.reservations().find(
        (row) => row.id === reservationId,
      );
      if (
        reservation &&
        !reservation.pendingCheckoutId &&
        this.isReservationAlreadyCheckedOut(reservation)
      ) {
        return true;
      }
    }

    if (assetId) {
      if (this.isAssetAlreadyCheckedOut(assetId)) {
        return true;
      }
      const activeCheckout = this.checkouts().find(
        (row) =>
          String(row.equipmentAssetID ?? '') === assetId &&
          (row.status === 'active' || row.status === 'overdue'),
      );
      if (activeCheckout) {
        return true;
      }
    }

    return false;
  }

  private resolveActiveCheckoutIdForAsset(assetId: string): string | undefined {
    const asset = this.assets().find((row) => row.id === assetId);
    if (asset?.activeCheckoutId) {
      return asset.activeCheckoutId;
    }

    if (this.isAssetAwaitingPhysicalIssue(assetId)) {
      return undefined;
    }

    const checkout = this.checkouts().find(
      (row) =>
        String(row.equipmentAssetID ?? '') === assetId &&
        (row.status === 'active' || row.status === 'overdue'),
    );
    return checkout?.id;
  }

  /** Paid reservation queued — not physically out on rental yet. */
  private isAssetAwaitingPhysicalIssue(assetId: string): boolean {
    const asset = this.assets().find((row) => row.id === assetId);
    if (!asset) {
      return false;
    }
    if (asset.status === 'awaiting-checkout') {
      return true;
    }
    if (asset.pendingCheckoutId && !asset.activeCheckoutId) {
      return true;
    }
    return false;
  }

  private isAssetAlreadyCheckedOut(assetId: string): boolean {
    if (this.isAssetAwaitingPhysicalIssue(assetId)) {
      return false;
    }

    const asset = this.assets().find((row) => row.id === assetId);
    if (asset?.status === 'checked-out') {
      return true;
    }

    return this.checkouts().some(
      (row) =>
        String(row.equipmentAssetID ?? '') === assetId &&
        (row.status === 'active' || row.status === 'overdue'),
    );
  }

  private isReservationOpenForCheckout(
    reservationId?: string,
    assetId?: string,
  ): boolean {
    if (reservationId) {
      const byId = this.reservations().find((row) => row.id === reservationId);
      if (
        byId &&
        this.shouldShowReservationOnCheckout(byId) &&
        !!byId.pendingCheckoutId
      ) {
        return true;
      }
      if (
        byId &&
        this.shouldShowReservationOnCheckout(byId) &&
        this.isReservationAwaitingPhysicalIssue(byId)
      ) {
        return true;
      }
    }

    // Do not treat "same asset id" as open-for-checkout — pooled rentals share
    // one placeholder AST row across many reservations.
    if (assetId && reservationId) {
      const byAsset = this.assets().find(
        (row) =>
          row.id === assetId &&
          row.reservationId === reservationId &&
          (row.status === 'awaiting-checkout' || !!row.pendingCheckoutId),
      );
      if (byAsset) {
        return true;
      }
    }

    return false;
  }

  private isQuantityBasedReservation(reservation: Reservation): boolean {
    if ((reservation.quantityRequested ?? 1) > 1) {
      return true;
    }
    if (!reservation.equipmentAssetID || reservation.equipmentAssetID <= 0) {
      return !!(reservation.inventoryItemID && reservation.inventoryItemID > 0);
    }
    return this.isQuantityBasedEquipment({
      id: reservation.equipmentAssetID,
      inventoryItemID: reservation.inventoryItemID,
    });
  }

  private isReservationAlreadyCheckedOut(reservation: Reservation): boolean {
    if (reservation.status === 'issued') {
      return true;
    }
    if (this.issuedReservationIds().has(reservation.id)) {
      return true;
    }

    if (this.isReservationAwaitingPhysicalIssue(reservation)) {
      return false;
    }

    // Pending paid-waiting id that has become a physical active checkout.
    if (reservation.pendingCheckoutId) {
      const linked = this.checkouts().find(
        (row) => row.id === reservation.pendingCheckoutId,
      );
      if (
        linked &&
        (linked.status === 'active' || linked.status === 'overdue')
      ) {
        return true;
      }
      return false;
    }

    // Checkout explicitly linked to this reservation (never shared by asset alone).
    const byReservation = this.checkouts().find(
      (row) =>
        (row.reservationId === reservation.id ||
          (!!reservation.reservationNumber &&
            row.reservationNumber === reservation.reservationNumber)) &&
        (row.status === 'active' || row.status === 'overdue'),
    );
    if (byReservation) {
      return true;
    }

    // Unique tagged unit only — pooled catalog items share one placeholder asset.
    if (this.isQuantityBasedReservation(reservation)) {
      return false;
    }

    const assetId = reservation.equipmentAssetID
      ? String(reservation.equipmentAssetID)
      : '';
    if (assetId && this.isAssetAlreadyCheckedOut(assetId)) {
      return true;
    }

    return false;
  }

  /** Paid + ready to issue — not the same as physically checked out. */
  private isReservationAwaitingPhysicalIssue(
    reservation: Reservation,
  ): boolean {
    if (reservation.status !== 'completed') {
      return false;
    }

    // Own pending paid-waiting id wins over shared placeholder asset status.
    if (reservation.pendingCheckoutId) {
      const physical = this.checkouts().find(
        (row) =>
          row.id === reservation.pendingCheckoutId &&
          (row.status === 'active' || row.status === 'overdue'),
      );
      if (!physical) {
        return true;
      }
      if (
        physical.reservationId &&
        physical.reservationId !== reservation.id &&
        physical.reservationNumber !== reservation.reservationNumber
      ) {
        return true;
      }
      return false;
    }

    const assetId = reservation.equipmentAssetID
      ? String(reservation.equipmentAssetID)
      : '';
    const assetByReservation =
      (assetId
        ? this.assets().find(
            (row) =>
              row.id === assetId &&
              (!row.reservationId || row.reservationId === reservation.id),
          )
        : undefined) ??
      (reservation.id
        ? this.assets().find((row) => row.reservationId === reservation.id)
        : undefined);

    if (
      assetByReservation?.status === 'checked-out' &&
      assetByReservation.activeCheckoutId
    ) {
      return false;
    }
    if (
      assetByReservation?.status === 'checked-out' &&
      !this.isQuantityBasedReservation(reservation)
    ) {
      return false;
    }

    const pendingCheckoutId =
      this.resolvePendingCheckoutIdForReservation(reservation);
    if (pendingCheckoutId) {
      return true;
    }

    return assetByReservation?.status === 'awaiting-checkout';
  }

  private isAlreadyCheckedOutError(error: unknown): boolean {
    const result = this.toApiError(error, '');
    return /already checked out/i.test(result.message ?? '');
  }

  /** DELETE /EquipmentCheckout/{id} — cancels and releases asset */
  deleteCheckoutViaApi(id: string): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.deleteCheckout(id);
      return of({ success: true });
    }

    const checkoutId = Number(id);
    if (!Number.isFinite(checkoutId) || checkoutId <= 0) {
      return of({
        success: false,
        message: 'Invalid checkout id. Refresh and try again.',
      });
    }

    return this.api.delete(`/EquipmentCheckout/${checkoutId}`).pipe(
      tap(() => this.refreshFromApi()),
      map(() => ({ success: true })),
      catchError((error) =>
        of(this.toApiError(error, 'Failed to cancel checkout.')),
      ),
    );
  }

  /** PUT /Damaged/{id} */
  updateDamagedRecordViaApi(
    id: string,
    patch: Partial<DamagedEquipmentRecord> & { notes?: string },
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.updateDamagedRecord(id, patch);
      return of({ success: true });
    }

    const reportId = Number(id);
    if (!Number.isFinite(reportId) || reportId <= 0) {
      return of({
        success: false,
        message: 'Invalid damage report id. Refresh and try again.',
      });
    }

    const statusMap: Record<DamagedEquipmentRecord['status'], string> = {
      open: 'Open',
      'under-review': 'Under_Review',
      resolved: 'Resolved',
    };

    return this.api
      .put(`/Damaged/${reportId}`, {
        status: statusMap[patch.status ?? 'open'] ?? 'Open',
        damageDescription: patch.equipment ?? null,
        chargeAmount: patch.estimatedCost ?? null,
        repairRequired:
          patch.damageType === 'damaged'
            ? true
            : patch.damageType === 'lost'
              ? false
              : null,
        quantityDamaged:
          patch.damageType === 'damaged'
            ? 1
            : patch.damageType === 'lost'
              ? 0
              : null,
        quantityMissing:
          patch.damageType === 'lost'
            ? 1
            : patch.damageType === 'damaged'
              ? 0
              : null,
        notes: patch.notes ?? null,
      })
      .pipe(
        tap(() => this.refreshFromApi()),
        map(() => ({ success: true })),
        catchError((error) =>
          of(this.toApiError(error, 'Failed to update damage report.')),
        ),
      );
  }

  /** PUT /Damaged/{id} with Resolved / Written_Off */
  resolveDamagedRecordViaApi(
    id: string,
    outcome: 'resolved' | 'written-off' = 'resolved',
  ): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      if (outcome === 'resolved') {
        this.resolveDamagedRecord(id);
      } else {
        this.updateDamagedRecord(id, {
          status: 'resolved',
          damageType: 'lost',
        });
      }
      return of({ success: true });
    }

    const reportId = Number(id);
    if (!Number.isFinite(reportId) || reportId <= 0) {
      return of({
        success: false,
        message: 'Invalid damage report id. Refresh and try again.',
      });
    }

    return this.api
      .put(`/Damaged/${reportId}`, {
        status: outcome === 'written-off' ? 'Written_Off' : 'Resolved',
      })
      .pipe(
        tap(() => this.refreshFromApi()),
        map(() => ({ success: true })),
        catchError((error) =>
          of(
            this.toApiError(
              error,
              `Failed to ${outcome === 'written-off' ? 'write off' : 'resolve'} report.`,
            ),
          ),
        ),
      );
  }

  /** DELETE /Damaged/{id} — soft-cancels */
  deleteDamagedRecordViaApi(id: string): Observable<EquipmentApiResult> {
    if (environment.useMockData) {
      this.deleteDamagedRecord(id);
      return of({ success: true });
    }

    const reportId = Number(id);
    if (!Number.isFinite(reportId) || reportId <= 0) {
      return of({
        success: false,
        message: 'Invalid damage report id. Refresh and try again.',
      });
    }

    return this.api.delete(`/Damaged/${reportId}`).pipe(
      tap(() => this.refreshFromApi()),
      map(() => ({ success: true })),
      catchError((error) =>
        of(this.toApiError(error, 'Failed to cancel damage report.')),
      ),
    );
  }

  private seedAssetsFromInventory(): void {
    if (!environment.useMockData) {
      // Live catalog rows are built from InventoryService in computed getters.
      return;
    }

    const rentable = this.inventory.rentableInventoryItems().map((item) => ({
      id: `inv-${item.id}`,
      assetId: item.code || `ITM-${item.id}`,
      name: item.name,
      serialNumber: item.code,
      category: item.category || 'Equipment',
      location: item.location || item.branch || '',
      status: 'available' as const,
      condition: 'good' as const,
      inventoryItemID: Number(item.id) || 0,
      branchID: Number(item.branchId) || undefined,
      isRentable: true,
    }));

    if (rentable.length) {
      this.mergeAssets(rentable);
    }
  }

  private isInventoryItemRentable(inventoryItemID?: number): boolean {
    if (!inventoryItemID || inventoryItemID <= 0) {
      return false;
    }
    const item = this.inventory.findItemById(inventoryItemID);
    return !!item && this.inventory.isEligibleForEquipmentRental(item);
  }

  private isAssetRentable(asset: EquipmentAsset): boolean {
    if (asset.isRentable === true) {
      return true;
    }
    if (asset.isRentable === false) {
      return false;
    }
    return this.isInventoryItemRentable(asset.inventoryItemID);
  }

  private resolveAssetIdForCheckout(checkout?: CheckoutRecord): number {
    if (!checkout) {
      return 0;
    }
    if (checkout.equipmentAssetID && checkout.equipmentAssetID > 0) {
      return checkout.equipmentAssetID;
    }
    const byName = this.assets().find(
      (row) =>
        row.name === checkout.equipment ||
        row.assetId === checkout.checkoutNumber ||
        row.id === checkout.id,
    );
    const n = Number(byName?.id);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  private toApiError(error: unknown, fallback: string): EquipmentApiResult {
    if (error instanceof HttpErrorResponse) {
      const body = error.error;
      if (typeof body === 'string' && body.trim()) {
        return { success: false, message: body };
      }
      if (typeof body === 'object' && body !== null) {
        const dict = body as Record<string, unknown>;
        const detailed =
          dict['error'] ??
          dict['Error'] ??
          dict['message'] ??
          dict['Message'] ??
          dict['title'];
        if (detailed != null && String(detailed).trim()) {
          return { success: false, message: String(detailed) };
        }
      }
      return { success: false, message: error.message || fallback };
    }
    return { success: false, message: fallback };
  }

  fetchAssets(
    query: ListQuery = {},
  ): Observable<PaginatedResult<EquipmentAsset>> {
    return of(this.paginate(this.filterAssets(this.assets(), query), query));
  }

  getAssetsByStatus(status: EquipmentStatus): EquipmentAsset[] {
    return this.assets().filter((asset) => asset.status === status);
  }

  getReservations(): Reservation[] {
    return this.reservations();
  }

  getCheckouts(): CheckoutRecord[] {
    return this.checkouts();
  }

  getOverdueCheckouts(): CheckoutRecord[] {
    return this.checkouts().filter((record) => record.status === 'overdue');
  }

  isReservationCheckedOut(reservation: Reservation): boolean {
    return this.isReservationAlreadyCheckedOut(reservation);
  }

  /**
   * Free units for walk-in issue (Current Stock on hand − reserved).
   * Reservations already hold QuantityReserved — do not hide the whole item.
   */
  walkInFreeQuantity(asset: EquipmentAsset, branchId?: string): number {
    const inventoryItemID =
      asset.inventoryItemID ??
      (String(asset.id).startsWith('inv-')
        ? Number(String(asset.id).slice(4)) || 0
        : 0);

    if (!this.isQuantityBasedEquipment(asset)) {
      return 1;
    }

    if (!inventoryItemID) {
      return 0;
    }

    const resolvedBranchId =
      branchId ||
      (asset.branchID != null ? String(asset.branchID) : undefined) ||
      defaultBranchFilterId(this.auth, this.settings.scopedBranches());

    // Walk-in Available Qty = free stock at the branch (on hand − reserved).
    // Location on the row is informational; issue still picks a stocked location.
    return this.inventory.availableQuantityAtBranch(
      inventoryItemID,
      resolvedBranchId && resolvedBranchId !== 'all'
        ? resolvedBranchId
        : undefined,
    );
  }

  isAssetAvailableForWalkInCheckout(asset: EquipmentAsset): boolean {
    if (asset.status !== 'available') {
      return false;
    }
    if (asset.reservationId || asset.pendingCheckoutId) {
      return false;
    }
    if (asset.canCheckout === false) {
      return false;
    }

    // Quantity / catalog rentals: show whenever free stock remains.
    // A reservation for the same item must NOT hide the whole walk-in row —
    // reserved qty is already deducted from Current Stock Available.
    if (this.isQuantityBasedEquipment(asset)) {
      return this.walkInFreeQuantity(asset) > 0;
    }

    const reservedByPaidQueue = this.buildReadyToIssueReservations().some(
      (row) => this.assetMatchesTaggedReservation(asset, row),
    );
    if (reservedByPaidQueue) {
      return false;
    }

    return !this.reservations().some(
      (row) =>
        row.status === 'completed' &&
        this.assetMatchesTaggedReservation(asset, row) &&
        !this.isReservationAlreadyCheckedOut(row),
    );
  }

  /** Match a tagged Asset Register unit to a reservation (not quantity catalog stock). */
  private assetMatchesTaggedReservation(
    asset: EquipmentAsset,
    reservation: Reservation,
  ): boolean {
    if (reservation.equipmentAssetID && reservation.equipmentAssetID > 0) {
      return String(reservation.equipmentAssetID) === asset.id;
    }
    return false;
  }

  private assetMatchesReservation(
    asset: EquipmentAsset,
    reservation: Reservation,
  ): boolean {
    if (String(reservation.equipmentAssetID ?? '') === asset.id) {
      return true;
    }
    const assetInvId =
      asset.inventoryItemID ??
      (String(asset.id).startsWith('inv-')
        ? Number(asset.id.slice(4)) || 0
        : 0);
    if (assetInvId > 0 && reservation.inventoryItemID === assetInvId) {
      return true;
    }
    if (
      asset.name &&
      reservation.equipment &&
      asset.name.trim().toLowerCase() ===
        reservation.equipment.trim().toLowerCase()
    ) {
      return true;
    }
    return false;
  }

  getCheckoutById(checkoutId: string): CheckoutRecord | undefined {
    return this.checkouts().find((row) => row.id === checkoutId);
  }

  findCheckoutForAsset(asset: EquipmentAsset): CheckoutRecord | undefined {
    const checkoutId = asset.activeCheckoutId;
    if (checkoutId) {
      const linked = this.getCheckoutById(checkoutId);
      if (linked) {
        return linked;
      }
    }

    return this.checkouts().find(
      (row) =>
        (row.status === 'active' || row.status === 'overdue') &&
        (String(row.equipmentAssetID ?? '') === asset.id ||
          row.equipment === asset.name ||
          row.checkoutNumber === asset.checkoutNumber),
    );
  }

  daysOverdueForCheckout(checkout: CheckoutRecord): number {
    if (checkout.daysOverdue && checkout.daysOverdue > 0) {
      return checkout.daysOverdue;
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(checkout.dueDate);
    due.setHours(0, 0, 0, 0);
    return Math.max(
      0,
      Math.ceil((today.getTime() - due.getTime()) / (1000 * 60 * 60 * 24)),
    );
  }

  isCheckoutOverdue(checkout: CheckoutRecord): boolean {
    return (
      checkout.status === 'overdue' || this.daysOverdueForCheckout(checkout) > 0
    );
  }

  getDamagedRecords(): DamagedEquipmentRecord[] {
    return this.damagedRecords();
  }

  updateAssetStatus(id: string, status: EquipmentStatus): void {
    this.assets.update((list) =>
      list.map((asset) => (asset.id === id ? { ...asset, status } : asset)),
    );
  }

  checkoutEquipment(
    assetId: string,
    customer: string,
    dueDate: string,
  ): CheckoutRecord | null {
    const asset = this.assets().find((row) => row.id === assetId);
    if (!asset || asset.status !== 'available') {
      return null;
    }

    const next = this.checkouts().length + 1;
    const record: CheckoutRecord = {
      id: crypto.randomUUID(),
      checkoutNumber: `CHK-2026-${String(112 + next).padStart(4, '0')}`,
      customer,
      equipment: asset.name,
      checkoutDate: new Date().toISOString().slice(0, 10),
      dueDate,
      status: 'active',
      equipmentAssetID: Number(asset.id) || undefined,
      inventoryItemID: asset.inventoryItemID,
    };

    this.checkouts.update((list) => [record, ...list]);
    this.updateAssetStatus(assetId, 'checked-out');
    return record;
  }

  returnEquipment(checkoutId: string): boolean {
    const checkout = this.checkouts().find((row) => row.id === checkoutId);
    if (!checkout || checkout.status === 'returned') {
      return false;
    }

    this.checkouts.update((list) =>
      list.map((row) =>
        row.id === checkoutId ? { ...row, status: 'returned' as const } : row,
      ),
    );

    const assetId = String(checkout.equipmentAssetID || '');
    const asset =
      this.assets().find((row) => row.id === assetId) ||
      this.assets().find((row) => row.name === checkout.equipment);
    if (asset) {
      this.updateAssetStatus(asset.id, 'available');
    }

    return true;
  }

  addReservation(
    reservation: Omit<Reservation, 'id' | 'reservationNumber'>,
  ): Reservation {
    const next = this.reservations().length + 1;
    const created: Reservation = {
      ...reservation,
      id: crypto.randomUUID(),
      reservationNumber: `RES-2026-${String(45 + next).padStart(4, '0')}`,
    };
    this.reservations.update((list) => [created, ...list]);
    return created;
  }

  addDamagedRecord(
    record: Omit<DamagedEquipmentRecord, 'id' | 'reportNumber'>,
  ): DamagedEquipmentRecord {
    const next = this.damagedRecords().length + 1;
    const created: DamagedEquipmentRecord = {
      ...record,
      id: crypto.randomUUID(),
      reportNumber: `DMG-2026-${String(8 + next).padStart(4, '0')}`,
    };
    this.damagedRecords.update((list) => [created, ...list]);

    const asset = this.assets().find((row) => row.name === record.equipment);
    if (asset) {
      this.updateAssetStatus(
        asset.id,
        record.damageType === 'lost' ? 'lost' : 'damaged',
      );
    }

    return created;
  }

  resolveDamagedRecord(id: string): void {
    this.damagedRecords.update((list) =>
      list.map((row) =>
        row.id === id ? { ...row, status: 'resolved' as const } : row,
      ),
    );
  }

  updateEquipmentAsset(id: string, patch: Partial<EquipmentAsset>): void {
    this.assets.update((list) =>
      list.map((asset) => (asset.id === id ? { ...asset, ...patch } : asset)),
    );
  }

  deleteEquipmentAsset(id: string): boolean {
    const asset = this.assets().find((row) => row.id === id);
    if (!asset || asset.status === 'checked-out') {
      return false;
    }
    this.assets.update((list) => list.filter((row) => row.id !== id));
    return true;
  }

  updateReservation(id: string, patch: Partial<Reservation>): void {
    this.reservations.update((list) =>
      list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
    if (
      patch.status === 'completed' ||
      patch.status === 'confirmed' ||
      patch.status === 'pending'
    ) {
      const reservation = this.reservations().find((row) => row.id === id);
      const assetId = reservation?.equipmentAssetID
        ? String(reservation.equipmentAssetID)
        : '';
      if (assetId) {
        this.assets.update((list) =>
          list.map((asset) =>
            asset.id === assetId
              ? {
                  ...asset,
                  status: 'awaiting-checkout' as const,
                  reservationId: id,
                  reservedCustomer:
                    reservation?.customer ?? asset.reservedCustomer,
                  reservedDueDate:
                    reservation?.endDate ?? asset.reservedDueDate,
                }
              : asset,
          ),
        );
      }
    }
  }

  deleteReservation(id: string): void {
    this.reservations.update((list) => list.filter((row) => row.id !== id));
  }

  updateCheckout(id: string, patch: Partial<CheckoutRecord>): void {
    this.checkouts.update((list) =>
      list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }

  deleteCheckout(id: string): boolean {
    const checkout = this.checkouts().find((row) => row.id === id);
    if (!checkout) {
      return false;
    }

    if (checkout.status === 'active' || checkout.status === 'overdue') {
      const asset =
        this.assets().find(
          (row) => row.id === String(checkout.equipmentAssetID || ''),
        ) || this.assets().find((row) => row.name === checkout.equipment);
      if (asset) {
        this.updateAssetStatus(asset.id, 'available');
      }
    }

    this.checkouts.update((list) => list.filter((row) => row.id !== id));
    return true;
  }

  updateDamagedRecord(
    id: string,
    patch: Partial<DamagedEquipmentRecord>,
  ): void {
    this.damagedRecords.update((list) =>
      list.map((row) => (row.id === id ? { ...row, ...patch } : row)),
    );
  }

  deleteDamagedRecord(id: string): void {
    this.damagedRecords.update((list) => list.filter((row) => row.id !== id));
  }

  private applyReservations(
    payload: GenericMultiResultDto,
    options: { skipQueueRebuild?: boolean } = {},
  ): void {
    this.settings.hydrateLookupsFromPayload(payload);

    let rows = findResultSetWithColumn(
      payload,
      'EquipmentReservationID',
      'StartDate',
    ).filter((row) => pickNumber(row, 'EquipmentReservationID') > 0);
    if (!rows.length) {
      rows = resultSetAt(payload, 3).filter(
        (row) => pickNumber(row, 'EquipmentReservationID') > 0,
      );
    }

    if (!rows.length) {
      const fallback = findResultSetWithColumn(
        payload,
        'ReservationNumber',
      ).filter((row) => {
        const id = pickNumber(row, 'EquipmentReservationID');
        const number = pickString(row, 'ReservationNumber', 'ReservationID');
        return id > 0 || !!number;
      });
      if (!fallback.length) {
        if (!options.skipQueueRebuild) {
          this.reservations.set([]);
        }
        return;
      }
      this.setReservationsFromRows(
        this.mergeReservationAssetIds(payload, fallback),
        options.skipQueueRebuild,
      );
      return;
    }

    this.setReservationsFromRows(
      this.mergeReservationAssetIds(payload, rows),
      options.skipQueueRebuild,
    );
  }

  /** Join reservation lines so each header row gets an EquipmentAssetID when present on lines. */
  private mergeReservationAssetIds(
    payload: GenericMultiResultDto,
    rows: unknown[],
  ): unknown[] {
    const lineRows = findResultSetWithColumn(
      payload,
      'EquipmentAssetID',
      'EquipmentReservationID',
    ).filter(
      (row) =>
        pickNumber(row, 'EquipmentReservationID') > 0 &&
        pickNumber(row, 'EquipmentAssetID') > 0,
    );
    if (!lineRows.length) {
      return rows;
    }

    const assetByReservation = new Map<number, number>();
    for (const line of lineRows) {
      const reservationId = pickNumber(line, 'EquipmentReservationID');
      const assetId = pickNumber(line, 'EquipmentAssetID');
      if (
        reservationId > 0 &&
        assetId > 0 &&
        !assetByReservation.has(reservationId)
      ) {
        assetByReservation.set(reservationId, assetId);
      }
    }

    return rows.map((row) => {
      const reservationId = pickNumber(row, 'EquipmentReservationID');
      const existingAssetId = pickNumber(row, 'EquipmentAssetID', 'AssetID');
      const mergedAssetId =
        existingAssetId > 0
          ? existingAssetId
          : (assetByReservation.get(reservationId) ?? 0);
      if (mergedAssetId <= 0) {
        return row;
      }
      return { ...asDict(row), EquipmentAssetID: mergedAssetId };
    });
  }

  private upsertReservationRow(
    row: unknown,
    extras: Partial<Reservation> = {},
  ): void {
    const merged = { ...asDict(row), ...extras };
    const [mapped] = this.mapReservationRows([merged]);
    if (!mapped?.id) {
      return;
    }

    this.reservations.update((list) => {
      const index = list.findIndex((item) => item.id === mapped.id);
      if (index >= 0) {
        const next = [...list];
        next[index] = { ...next[index], ...mapped };
        return next;
      }
      return [mapped, ...list];
    });
  }

  private setReservationsFromRows(
    rows: unknown[],
    skipQueueRebuild = false,
  ): void {
    const previousById = new Map(
      this.reservations().map((row) => [row.id, row] as const),
    );

    this.reservations.set(
      this.filterReservationsForUser(
        this.mapReservationRows(rows).map((row) => {
          const enriched = this.enrichReservationWithAssetId(row);
          const previous = previousById.get(enriched.id);
          const issued = this.issuedReservationIds().has(enriched.id);
          if (!previous && !issued) {
            return enriched;
          }

          // Keep known-good display fields when the list API returns blanks
          // (e.g. older rows without StoreLocationID) so edit/list stay stable.
          return {
            ...enriched,
            customer: enriched.customer?.trim()
              ? enriched.customer
              : (previous?.customer ?? enriched.customer),
            equipment: enriched.equipment?.trim()
              ? enriched.equipment
              : (previous?.equipment ?? enriched.equipment),
            branch: enriched.branch?.trim()
              ? enriched.branch
              : previous?.branch,
            branchId: enriched.branchId || previous?.branchId,
            location: enriched.location?.trim()
              ? enriched.location
              : previous?.location,
            locationId: enriched.locationId || previous?.locationId,
            quantityRequested:
              enriched.quantityRequested && enriched.quantityRequested > 0
                ? enriched.quantityRequested
                : previous?.quantityRequested,
            // Issued this session: never restore a stale paid-waiting id from API lag.
            pendingCheckoutId: issued ? undefined : enriched.pendingCheckoutId,
          };
        }),
      ),
    );
    if (!skipQueueRebuild) {
      // Issue Equipment list is computed from reservations + assets.
    }
  }

  /** Branch-scoped reservation list (matches Reservations page filters). */
  private filterReservationsForUser(rows: Reservation[]): Reservation[] {
    const userBranchId = this.auth.currentBranchId();
    if (userBranchId <= 0) {
      return rows;
    }

    const branches = this.settings.scopedBranches();
    const branchFilter = String(userBranchId);
    return rows.filter((row) =>
      matchesBranchFilter(branchFilter, row.branch, row.branchId, branches),
    );
  }

  /** Fill missing equipmentAssetID by matching reservation equipment to asset register rows. */
  private enrichReservationWithAssetId(reservation: Reservation): Reservation {
    if (reservation.equipmentAssetID && reservation.equipmentAssetID > 0) {
      return reservation;
    }

    // Quantity catalog rentals must stay asset-less. Attaching the shared
    // AST-{item}-B{branch} placeholder makes one physical issue look like
    // every other reservation for the same item was checked out.
    if (this.isQuantityBasedReservation(reservation)) {
      const linkedOnly = this.assets().find(
        (asset) =>
          asset.reservationId === reservation.id && !!asset.pendingCheckoutId,
      );
      if (linkedOnly) {
        const linkedId = this.resolveEquipmentAssetId(linkedOnly.id, {
          assetTag: linkedOnly.assetId,
          equipmentName: linkedOnly.name,
          inventoryItemID: linkedOnly.inventoryItemID,
        });
        if (linkedId > 0) {
          return {
            ...reservation,
            equipmentAssetID: linkedId,
            inventoryItemID:
              reservation.inventoryItemID ?? linkedOnly.inventoryItemID,
          };
        }
      }
      return reservation;
    }

    const linked = this.assets().find(
      (asset) => asset.reservationId === reservation.id,
    );
    if (linked) {
      const linkedId = this.resolveEquipmentAssetId(linked.id, {
        assetTag: linked.assetId,
        equipmentName: linked.name,
        inventoryItemID: linked.inventoryItemID,
      });
      if (linkedId > 0) {
        return {
          ...reservation,
          equipmentAssetID: linkedId,
          inventoryItemID:
            reservation.inventoryItemID ?? linked.inventoryItemID,
        };
      }
    }

    const needle = reservation.equipment?.trim().toLowerCase();
    if (!needle) {
      return reservation;
    }

    const asset = this.assets().find(
      (row) =>
        !this.isPooledPlaceholderAsset(row) &&
        (row.name.trim().toLowerCase() === needle ||
          row.assetId.trim().toLowerCase() === needle),
    );
    if (!asset) {
      return reservation;
    }

    const resolvedId = this.resolveEquipmentAssetId(asset.id, {
      assetTag: asset.assetId,
      equipmentName: asset.name,
      inventoryItemID: asset.inventoryItemID,
    });

    return {
      ...reservation,
      equipmentAssetID: resolvedId > 0 ? resolvedId : undefined,
      inventoryItemID: reservation.inventoryItemID ?? asset.inventoryItemID,
    };
  }

  /** Reservations paid and ready for physical issue on Check-Out (plus recently issued). */
  private shouldShowReservationOnCheckout(reservation: Reservation): boolean {
    return (
      reservation.status === 'completed' || reservation.status === 'issued'
    );
  }

  private mapReservationRows(rows: unknown[]): Reservation[] {
    return rows.map((row, index) => {
      const statusRaw = pickString(
        row,
        'ReservationStatus',
        'Status',
        'status',
      ).toLowerCase();
      const id =
        pickString(row, 'EquipmentReservationID') ||
        pickString(row, 'ReservationID', 'id') ||
        String(index + 1);
      return {
        id,
        reservationNumber:
          pickString(row, 'ReservationNumber', 'reservationNumber') ||
          `RES-${index + 1}`,
        customer: pickString(
          row,
          'ClientFullName',
          'Customer',
          'RequestedBy',
          'customer',
          'Notes',
        ),
        equipment: pickString(
          row,
          'ItemName',
          'Asset',
          'equipment',
          'AssetNumber',
        ),
        branch: pickString(row, 'BranchName', 'branch'),
        branchId: pickString(row, 'BranchID', 'branchID', 'branchId'),
        location: pickString(row, 'LocationName', 'location'),
        locationId: (() => {
          const id = pickNumber(
            row,
            'StoreLocationID',
            'LocationID',
            'locationId',
          );
          return id > 0 ? String(id) : undefined;
        })(),
        startDate: pickDate(row, 'RequiredFromDate', 'StartDate', 'startDate'),
        endDate: pickDate(row, 'RequiredToDate', 'EndDate', 'endDate'),
        status: statusRaw.includes('cancel')
          ? ('cancelled' as const)
          : statusRaw.includes('issued')
            ? ('issued' as const)
            : statusRaw.includes('complete')
              ? ('completed' as const)
              : statusRaw.includes('confirm')
                ? ('confirmed' as const)
                : ('pending' as const),
        quantityRequested:
          pickNumber(row, 'QuantityRequested', 'quantityRequested') ||
          undefined,
        equipmentAssetID:
          pickNumber(
            row,
            'EquipmentAssetID',
            'AssetID',
            'assetID',
            'equipmentAssetID',
          ) || undefined,
        inventoryItemID: pickNumber(row, 'InventoryItemID') || undefined,
        pendingCheckoutId: (() => {
          const numericId = pickNumber(
            row,
            'PendingCheckoutID',
            'pendingCheckoutId',
          );
          if (numericId > 0) {
            return String(numericId);
          }
          return (
            pickString(row, 'PendingCheckoutID', 'pendingCheckoutId') ||
            undefined
          );
        })(),
      };
    });
  }

  private applyOverdue(payload: GenericMultiResultDto): void {
    const rows =
      firstResultSet(payload).length > 0
        ? firstResultSet(payload)
        : resultSetAt(payload, 0);

    if (!rows.length) {
      return;
    }

    const overdue = this.mapCheckoutRows(rows).map((row) => ({
      ...row,
      status: 'overdue' as const,
    }));

    const others = this.checkouts().filter((row) => row.status !== 'overdue');
    const map = new Map<string, CheckoutRecord>();
    for (const row of [...others, ...overdue]) {
      map.set(row.id || row.checkoutNumber, row);
    }
    this.checkouts.set([...map.values()]);
  }

  private mapCheckoutRows(rows: unknown[]): CheckoutRecord[] {
    const todayIso = new Date().toISOString().slice(0, 10);

    return rows.map((row, index) => {
      const dueDate = pickDate(row, 'ExpectedReturnDate', 'DueDate', 'dueDate');
      const daysOverdue = pickNumber(row, 'DaysOverdue');
      const isOverdue =
        pickNumber(row, 'IsOverdue') === 1 ||
        daysOverdue > 0 ||
        (!!dueDate && dueDate < todayIso);
      const checkoutId = pickNumber(row, 'EquipmentCheckoutID');
      const customer =
        pickString(
          row,
          'ClientFullName',
          'Customer',
          'ReceivedByName',
          'customer',
        ) ||
        pickString(row, 'ReceivedByName') ||
        'Customer';

      return {
        id:
          checkoutId > 0
            ? String(checkoutId)
            : pickString(row, 'CheckoutNumber', 'id') || String(index + 1),
        checkoutNumber:
          pickString(row, 'CheckoutNumber', 'checkoutNumber') ||
          `CHK-${index + 1}`,
        customer,
        equipment:
          pickString(row, 'ItemName', 'Asset', 'equipment') || 'Equipment',
        receiver: pickString(row, 'ReceivedByName', 'receiver'),
        receiverPhone: pickString(
          row,
          'ReceivedByPhone',
          'ClientPhone',
          'receiverPhone',
        ),
        vehicleRegistration: pickString(row, 'VehicleRegistration'),
        branch: pickString(row, 'BranchName', 'branch'),
        branchId: pickString(row, 'BranchID', 'branchID', 'branchId'),
        location: pickString(row, 'LocationName', 'location'),
        destination: pickString(
          row,
          'DestinationLocation',
          'destination',
          'Destination',
        ),
        assetNumber: pickString(row, 'AssetNumber', 'ItemCode'),
        serialNumber: pickString(row, 'SerialNumber'),
        category: pickString(row, 'CategoryName', 'category'),
        reservationNumber: pickString(row, 'ReservationNumber'),
        caseNumber: pickString(row, 'FuneralCaseNumber', 'CaseNumber'),
        checkoutDate: pickDate(row, 'CheckoutDate', 'checkoutDate'),
        dueDate,
        daysOverdue: daysOverdue > 0 ? daysOverdue : undefined,
        status: isOverdue ? ('overdue' as const) : ('active' as const),
        equipmentAssetID: pickNumber(row, 'EquipmentAssetID') || undefined,
        inventoryItemID: pickNumber(row, 'InventoryItemID') || undefined,
        reservationId: (() => {
          const id = pickNumber(row, 'EquipmentReservationID');
          if (id > 0) {
            return String(id);
          }
          return (
            pickString(row, 'EquipmentReservationID', 'reservationId') ||
            undefined
          );
        })(),
        quantity: (() => {
          const qty = pickNumber(
            row,
            'QuantityCheckedOut',
            'Quantity',
            'quantity',
          );
          return qty > 0 ? qty : 1;
        })(),
        quantityReturned: (() => {
          const qty = pickNumber(
            row,
            'QuantityReturnedToDate',
            'QuantityReturned',
          );
          return qty >= 0 ? qty : 0;
        })(),
        quantityOutstanding: (() => {
          // Prefer the API value even when it is 0 (fully returned).
          const hasOutstandingCol =
            pick(row, 'QuantityOutstanding', 'quantityOutstanding') !==
            undefined;
          if (hasOutstandingCol) {
            return Math.max(
              0,
              pickNumber(row, 'QuantityOutstanding', 'quantityOutstanding'),
            );
          }
          const out =
            pickNumber(row, 'QuantityCheckedOut', 'Quantity', 'quantity') || 1;
          const returned =
            pickNumber(row, 'QuantityReturnedToDate', 'QuantityReturned') || 0;
          const missing =
            pickNumber(row, 'QuantityMissingToDate', 'QuantityMissing') || 0;
          return Math.max(0, out - returned - missing);
        })(),
      };
    });
  }

  /**
   * usp_IMS_GetEquipmentCheckout:
   * Summary = KPIs, ResultSets[0] = assets (+ checkout columns when checked out).
   */
  private applyCheckouts(
    payload: GenericMultiResultDto,
    options: { skipQueueRebuild?: boolean } = {},
  ): void {
    this.settings.hydrateLookupsFromPayload(payload);

    const rows =
      findResultSetWithColumn(
        payload,
        'EquipmentAssetID',
        'CanCheckout',
        'DisplayStatus',
      ) || resultSetAt(payload, 0);
    if (!rows.length) {
      return;
    }

    const todayIso = new Date().toISOString().slice(0, 10);
    const assets: EquipmentAsset[] = [];
    const checkouts: CheckoutRecord[] = [];
    const seenCheckouts = new Set<string>();

    for (const row of rows) {
      const equipmentAssetIdNum = pickNumber(row, 'EquipmentAssetID');
      if (equipmentAssetIdNum <= 0) {
        continue;
      }
      const assetId = String(equipmentAssetIdNum);

      const checkoutNotes = pickString(row, 'CheckoutNotes', 'Notes');
      const statusRaw = pickString(
        row,
        'AssetStatus',
        'DisplayStatus',
        'status',
      ).toLowerCase();
      const checkoutId =
        pickNumber(row, 'EquipmentCheckoutID') > 0
          ? String(pickNumber(row, 'EquipmentCheckoutID'))
          : pickString(row, 'EquipmentCheckoutID');
      const dueDate = pickDate(row, 'ExpectedReturnDate', 'DueDate', 'dueDate');
      const canCheckout =
        pickString(row, 'CanCheckout').toUpperCase() === 'YES';
      const displayStatus = pickString(row, 'DisplayStatus').toLowerCase();
      const checkoutStatus = pickString(
        row,
        'CheckoutStatus',
        'checkoutStatus',
      ).toUpperCase();
      const reservationId = pickString(row, 'EquipmentReservationID');
      const hasReservation =
        pickNumber(row, 'EquipmentReservationID') > 0 || !!reservationId;
      const isPaidWaiting =
        checkoutNotes.includes('[[PAID_WAITING]]') ||
        displayStatus.includes('awaiting checkout') ||
        (displayStatus.includes('paid') &&
          displayStatus.includes('awaiting')) ||
        checkoutStatus === 'PAID_WAITING' ||
        checkoutStatus === 'PENDING';
      let isCheckedOut =
        !isPaidWaiting &&
        (checkoutStatus === 'CHECKED_OUT' ||
          (displayStatus.includes('checked out') &&
            pickString(row, 'AssetStatus')
              .toLowerCase()
              .replace(/\s+/g, '_') === 'checked_out'));
      const reservationAwaitingCheckout = this.isReservationOpenForCheckout(
        reservationId,
        assetId,
      );
      if (
        isCheckedOut &&
        reservationAwaitingCheckout &&
        (isPaidWaiting ||
          displayStatus.includes('awaiting checkout') ||
          checkoutNotes.includes('[[PAID_WAITING]]'))
      ) {
        isCheckedOut = false;
      }
      const awaitingPhysicalIssue =
        isPaidWaiting ||
        (reservationAwaitingCheckout && !!checkoutId && !isCheckedOut);
      const isReservedForCheckout =
        reservationAwaitingCheckout ||
        (hasReservation &&
          !isCheckedOut &&
          !isPaidWaiting &&
          !canCheckout &&
          (displayStatus.includes('reserved') ||
            statusRaw.includes('reserved')));

      let assetStatus: EquipmentStatus = 'available';
      if (awaitingPhysicalIssue) {
        assetStatus = 'awaiting-checkout';
      } else if (canCheckout) {
        assetStatus = 'available';
      } else if (isCheckedOut) {
        assetStatus = 'checked-out';
      } else if (isReservedForCheckout || reservationAwaitingCheckout) {
        assetStatus = 'awaiting-checkout';
      } else if (statusRaw.includes('lost')) {
        assetStatus = 'lost';
      } else if (statusRaw.includes('damage') || statusRaw.includes('repair')) {
        assetStatus = statusRaw.includes('repair') ? 'in-repair' : 'damaged';
      } else if (statusRaw.includes('checked')) {
        assetStatus = 'checked-out';
      } else if (
        displayStatus.includes('available') ||
        statusRaw.replace(/\s+/g, '_').includes('available') ||
        statusRaw.includes('reserved')
      ) {
        assetStatus = 'available';
      }

      // Stale register/checkout mismatch: no active checkout row but marked checked out.
      if (
        !checkoutId &&
        assetStatus === 'checked-out' &&
        (statusRaw.includes('available') || displayStatus.includes('available'))
      ) {
        assetStatus = 'available';
      }

      const inventoryItemID = pickNumber(row, 'InventoryItemID') || undefined;
      const itemType = pickString(row, 'ItemType').toUpperCase();
      const isRentableFlag =
        pickNumber(row, 'IsRentable') === 1 ||
        pickString(row, 'IsRentable').toLowerCase() === 'true' ||
        itemType.includes('RENTAL');
      // Trust Item Master IsRentable even for consumable-type items used as rentals.
      const isRentable = isRentableFlag;

      const resolvedCanCheckout =
        canCheckout ||
        (assetStatus === 'available' && !isCheckedOut && !isPaidWaiting);

      assets.push({
        id: assetId,
        assetId: pickString(row, 'AssetNumber', 'ItemCode') || `AST-${assetId}`,
        name:
          pickString(row, 'ItemName', 'Asset', 'name') || `Asset ${assetId}`,
        serialNumber: pickString(row, 'SerialNumber'),
        category: pickString(row, 'CategoryName', 'category') || 'Equipment',
        location: pickString(row, 'LocationName', 'BranchName', 'location'),
        status: assetStatus,
        condition: 'good',
        inventoryItemID,
        branchID: pickNumber(row, 'BranchID') || undefined,
        branchName: pickString(row, 'BranchName', 'branch'),
        locationID:
          pickNumber(row, 'StoreLocationID', 'LocationID', 'locationID') ||
          undefined,
        canCheckout: resolvedCanCheckout,
        isRentable,
        pendingCheckoutId: awaitingPhysicalIssue
          ? checkoutId || undefined
          : undefined,
        activeCheckoutId:
          isCheckedOut && !awaitingPhysicalIssue
            ? checkoutId || undefined
            : undefined,
        reservationId: reservationId || undefined,
        reservedCustomer:
          pickString(
            row,
            'ReceivedByName',
            'ClientFullName',
            'Customer',
            'RequestedBy',
          ) || undefined,
        reservedDueDate: dueDate || undefined,
        checkedOutCustomer: isCheckedOut
          ? pickString(row, 'ReceivedByName', 'ClientFullName', 'Customer') ||
            undefined
          : undefined,
        checkedOutPhone: isCheckedOut
          ? pickString(row, 'ReceivedByPhone', 'ClientPhone') || undefined
          : undefined,
        checkedOutDate: isCheckedOut
          ? pickDate(row, 'CheckoutDate', 'checkoutDate') || undefined
          : undefined,
        checkoutNumber: isCheckedOut
          ? pickString(row, 'CheckoutNumber') || undefined
          : undefined,
        vehicleRegistration: isCheckedOut
          ? pickString(row, 'VehicleRegistration') || undefined
          : undefined,
      });

      if (
        isCheckedOut &&
        !awaitingPhysicalIssue &&
        !seenCheckouts.has(checkoutId)
      ) {
        seenCheckouts.add(checkoutId);
        const [mapped] = this.mapCheckoutRows([row]);
        if (mapped) {
          checkouts.push(mapped);
        }
      }
    }

    const registerAssets =
      this.inventory.allItems().length > 0
        ? assets.filter((asset) => this.isRegisterAssetBackedByInventory(asset))
        : assets;

    this.mergeAssets(registerAssets);
    this.clearPendingCheckoutOnIssuedReservations(registerAssets);
    this.applyPaidWaitingQueueFromPayload(payload);
    this.syncCheckoutsFromAssetGrid();
    if (checkouts.length) {
      const map = new Map<string, CheckoutRecord>();
      for (const row of checkouts) {
        map.set(row.id, row);
      }
      for (const row of this.checkouts()) {
        if (!map.has(row.id)) {
          map.set(row.id, row);
        }
      }
      this.checkouts.set(
        filterRowsByUserBranch(
          this.auth,
          [...map.values()],
          (row) => row.branchId,
        ),
      );
    }
  }

  private applyDamaged(payload: GenericMultiResultDto): void {
    const rows = findResultSetWithColumn(
      payload,
      'EquipmentReturnID',
      'ReturnNumber',
      'ReportType',
    );
    const damagedRecords: DamagedEquipmentRecord[] = [];

    rows.forEach((row, index) => {
      const reportType = pickString(
        row,
        'ReportType',
        'damageType',
      ).toLowerCase();
      const statusRaw = pickString(
        row,
        'Status',
        'ReturnStatus',
        'AssetStatus',
      ).toLowerCase();
      const id =
        pickString(row, 'EquipmentReturnID', 'id') || String(index + 1);
      const assetId = pickString(row, 'EquipmentAssetID') || String(index + 1);
      const equipmentAssetIdNum = pickNumber(row, 'EquipmentAssetID');
      const assetNumber = pickString(
        row,
        'AssetNumber',
        'SerialNumber',
        'ReturnNumber',
      );
      const equipment = pickString(
        row,
        'ItemName',
        'Asset',
        'equipment',
        'AssetNumber',
      );
      const damageType =
        reportType.includes('lost') || statusRaw.includes('lost')
          ? ('lost' as const)
          : ('damaged' as const);
      const recordStatus =
        statusRaw.includes('resolv') || statusRaw.includes('written')
          ? ('resolved' as const)
          : statusRaw.includes('review')
            ? ('under-review' as const)
            : ('open' as const);

      damagedRecords.push({
        id,
        reportNumber:
          pickString(row, 'ReturnNumber') || assetNumber || `DMG-${index + 1}`,
        equipment:
          equipment ||
          pickString(row, 'ItemCode') ||
          assetNumber ||
          `Report ${id}`,
        reportedDate:
          pickDate(row, 'ReportedDate', 'ReturnDate', 'reportedDate') ||
          today(),
        damageType,
        status: recordStatus,
        estimatedCost: pickNumber(
          row,
          'ChargeAmount',
          'PurchaseCost',
          'estimatedCost',
        ),
        equipmentAssetID:
          equipmentAssetIdNum > 0 ? equipmentAssetIdNum : undefined,
        inventoryItemID: (() => {
          const n = pickNumber(row, 'InventoryItemID');
          return n > 0 ? n : undefined;
        })(),
        itemCode: pickString(row, 'ItemCode') || undefined,
        assetNumber: assetNumber || undefined,
        checkoutNumber: pickString(row, 'CheckoutNumber') || undefined,
        branchName: pickString(row, 'BranchName') || undefined,
        locationName: pickString(row, 'LocationName') || undefined,
        destination:
          pickString(row, 'DestinationLocation', 'Destination') || undefined,
        conditionIn: pickString(row, 'ConditionIn') || undefined,
        damageDescription: pickString(row, 'DamageDescription') || undefined,
        quantityReturned: pickNumber(row, 'QuantityReturned'),
        quantityDamaged: pickNumber(row, 'QuantityDamaged'),
        quantityMissing: pickNumber(row, 'QuantityMissing'),
        repairRequired:
          pickString(row, 'CanResolve', 'RepairRequired').toUpperCase() ===
            'YES' || pickNumber(row, 'RepairRequired') === 1,
      });
    });

    this.damagedRecords.set(
      filterRowsByUserBranch(
        this.auth,
        damagedRecords,
        (row) =>
          (row as DamagedEquipmentRecord & { branchId?: string }).branchId,
      ),
    );
    // Damaged/lost assets belong on the Damaged page — do not merge into checkout pool.
  }

  private syncCheckoutsFromAssetGrid(): void {
    const todayIso = new Date().toISOString().slice(0, 10);
    const active = this.assets()
      .filter((asset) => asset.status === 'checked-out')
      .flatMap((asset) => {
        const checkoutId = asset.activeCheckoutId ?? '';
        if (!checkoutId) {
          return [];
        }
        const dueDate = asset.reservedDueDate ?? '';
        const status =
          dueDate && dueDate < todayIso
            ? ('overdue' as const)
            : ('active' as const);
        const daysOverdue =
          status === 'overdue' && dueDate
            ? Math.max(
                0,
                Math.ceil(
                  (Date.parse(todayIso) - Date.parse(dueDate)) /
                    (1000 * 60 * 60 * 24),
                ),
              )
            : undefined;

        return [
          {
            id: checkoutId,
            checkoutNumber: asset.checkoutNumber ?? `CHK-${checkoutId}`,
            customer:
              asset.checkedOutCustomer ?? asset.reservedCustomer ?? 'Customer',
            equipment: asset.name,
            receiver: asset.checkedOutCustomer,
            receiverPhone: asset.checkedOutPhone,
            vehicleRegistration: asset.vehicleRegistration,
            branch: asset.branchName,
            branchId: asset.branchID ? String(asset.branchID) : undefined,
            location: asset.location,
            assetNumber: asset.assetId,
            serialNumber: asset.serialNumber,
            category: asset.category,
            checkoutDate: asset.checkedOutDate ?? '',
            dueDate,
            daysOverdue,
            status,
            equipmentAssetID: Number(asset.id) || undefined,
            inventoryItemID: asset.inventoryItemID,
          } satisfies CheckoutRecord,
        ];
      });

    if (!active.length) {
      return;
    }

    const map = new Map<string, CheckoutRecord>();
    for (const row of this.checkouts()) {
      map.set(row.id, row);
    }
    for (const row of active) {
      map.set(row.id, row);
    }
    this.checkouts.set([...map.values()]);
  }

  /** Ready-to-issue list — always mirrors completed reservations on the Reservations page. */
  private buildReadyToIssueReservations(): Reservation[] {
    return this.buildPaidReservationsForIssuePage().filter(
      (reservation) => !this.isReservationAlreadyCheckedOut(reservation),
    );
  }

  /** All completed reservations for Issue Equipment (ready + checked out). */
  private buildPaidReservationsForIssuePage(): Reservation[] {
    const map = new Map<string, Reservation>();

    for (const reservation of this.filterReservationsForUser(
      this.reservations(),
    )) {
      if (!this.shouldShowReservationOnCheckout(reservation)) {
        continue;
      }

      const enriched = this.enrichReservationWithAssetId(reservation);
      const pendingCheckoutId =
        enriched.pendingCheckoutId ??
        this.resolveCheckoutIdForRelease(
          enriched.equipmentAssetID
            ? String(enriched.equipmentAssetID)
            : undefined,
          enriched.id,
        );
      map.set(enriched.id, {
        ...enriched,
        pendingCheckoutId,
      });
    }

    for (const asset of this.assets()) {
      if (asset.status !== 'awaiting-checkout' || !asset.reservationId) {
        continue;
      }
      const existing = map.get(asset.reservationId);
      if (!existing) {
        continue;
      }
      map.set(asset.reservationId, {
        ...existing,
        equipmentAssetID: Number(asset.id) || existing.equipmentAssetID,
        inventoryItemID: asset.inventoryItemID ?? existing.inventoryItemID,
        pendingCheckoutId:
          asset.pendingCheckoutId ?? existing.pendingCheckoutId,
        branch: existing.branch || asset.branchName || existing.branch,
        location: existing.location || asset.location || existing.location,
      });
    }

    return [...map.values()].sort((a, b) => {
      const aCheckedOut = this.isReservationAlreadyCheckedOut(a);
      const bCheckedOut = this.isReservationAlreadyCheckedOut(b);
      if (aCheckedOut !== bCheckedOut) {
        return aCheckedOut ? 1 : -1;
      }
      return a.reservationNumber.localeCompare(b.reservationNumber);
    });
  }

  /** Numeric IMS.AssetRegister.EquipmentAssetID — never use barcode/tag as id. */
  private resolveEquipmentAssetId(
    candidate?: string | number,
    hints: {
      assetTag?: string;
      equipmentName?: string;
      inventoryItemID?: number;
    } = {},
  ): number {
    const raw = String(candidate ?? '').trim();
    if (raw.startsWith('inv-') || raw.startsWith('res-')) {
      candidate = undefined;
    }

    const direct = Number(candidate);
    if (Number.isFinite(direct) && direct > 0) {
      return direct;
    }

    if (raw) {
      const byRegisterId = this.assets().find((row) => row.id === raw);
      const fromRow = Number(byRegisterId?.id);
      if (Number.isFinite(fromRow) && fromRow > 0) {
        return fromRow;
      }
    }

    const tag = hints.assetTag?.trim().toLowerCase();
    if (tag) {
      const byTag = this.assets().find(
        (row) =>
          !this.isPooledPlaceholderAsset(row) &&
          row.assetId.trim().toLowerCase() === tag,
      );
      const fromTag = Number(byTag?.id);
      if (Number.isFinite(fromTag) && fromTag > 0) {
        return fromTag;
      }
    }

    const name = hints.equipmentName?.trim().toLowerCase();
    if (name) {
      const byName = this.assets().find(
        (row) =>
          !this.isPooledPlaceholderAsset(row) &&
          !String(row.id).startsWith('inv-') &&
          row.name.trim().toLowerCase() === name,
      );
      const fromName = Number(byName?.id);
      if (Number.isFinite(fromName) && fromName > 0) {
        return fromName;
      }
    }

    if (hints.inventoryItemID && hints.inventoryItemID > 0) {
      const byItem = this.assets().find(
        (row) =>
          !this.isPooledPlaceholderAsset(row) &&
          !String(row.id).startsWith('inv-') &&
          row.inventoryItemID === hints.inventoryItemID,
      );
      const fromItem = Number(byItem?.id);
      if (Number.isFinite(fromItem) && fromItem > 0) {
        return fromItem;
      }
    }

    return 0;
  }

  private resolveInventoryItemIdForReservation(
    reservation: Pick<
      Reservation,
      'equipmentAssetID' | 'inventoryItemID' | 'equipment'
    >,
  ): number {
    const fromField = Number(reservation.inventoryItemID) || 0;
    if (fromField > 0) {
      return fromField;
    }

    const rawAsset = String(reservation.equipmentAssetID ?? '');
    if (rawAsset.startsWith('inv-')) {
      return Number(rawAsset.slice(4)) || 0;
    }

    const asset = this.assets().find(
      (row) =>
        row.id === rawAsset ||
        row.assetId === rawAsset ||
        row.name === reservation.equipment,
    );
    return Number(asset?.inventoryItemID) || 0;
  }

  private resolveEquipmentAssetIdForReservation(
    equipmentAssetID?: number,
    inventoryItemID?: number,
    branchID?: number,
  ): number {
    const raw = String(equipmentAssetID ?? '');
    if (raw.startsWith('inv-')) {
      const invId = Number(raw.slice(4)) || inventoryItemID || 0;
      const byInventory = this.findRegisterAssetByInventoryItem(
        invId,
        branchID,
      );
      return Number(byInventory?.id) || 0;
    }

    const direct = this.resolveEquipmentAssetId(equipmentAssetID, {
      inventoryItemID,
    });
    if (direct > 0) {
      return direct;
    }

    if (inventoryItemID && inventoryItemID > 0) {
      const byItem = this.findRegisterAssetByInventoryItem(
        inventoryItemID,
        branchID,
      );
      return Number(byItem?.id) || 0;
    }

    return 0;
  }

  private findRegisterAssetByInventoryItem(
    inventoryItemID: number,
    branchID?: number,
  ): EquipmentAsset | undefined {
    return this.assets().find((row) => {
      if (String(row.id).startsWith('inv-')) {
        return false;
      }
      if (Number(row.inventoryItemID) !== inventoryItemID) {
        return false;
      }
      if (Number(row.id) <= 0) {
        return false;
      }
      if (
        branchID &&
        branchID > 0 &&
        row.branchID &&
        row.branchID !== branchID
      ) {
        return false;
      }
      if (branchID && branchID > 0 && !row.branchID && row.branchName) {
        const branch = this.settings
          .scopedBranches()
          .find((b) => b.id === String(branchID));
        const expected = (branch?.name ?? '').trim().toLowerCase();
        const actual = row.branchName.trim().toLowerCase();
        if (
          expected &&
          actual &&
          expected !== actual &&
          !actual.includes(expected)
        ) {
          return false;
        }
      }
      return true;
    });
  }

  private mergeAssets(incoming: EquipmentAsset[]): void {
    if (!incoming.length) {
      return;
    }

    const map = new Map<string, EquipmentAsset>();
    for (const asset of this.assets()) {
      if (
        asset.id.startsWith('inv-') &&
        incoming.some(
          (row) => row.name === asset.name && !row.id.startsWith('inv-'),
        )
      ) {
        continue;
      }
      map.set(asset.id || asset.assetId, asset);
    }
    for (const asset of incoming) {
      map.set(asset.id || asset.assetId, asset);
    }
    this.assets.set(
      filterRowsByUserBranch(this.auth, [...map.values()], (asset) =>
        asset.branchID != null ? String(asset.branchID) : undefined,
      ),
    );
  }

  /** Drop stale paid-waiting ids only for reservations whose own pending checkout was issued. */
  private clearPendingCheckoutOnIssuedReservations(
    assets: EquipmentAsset[],
  ): void {
    const issuedCheckoutIds = new Set(
      assets
        .filter(
          (asset) => asset.status === 'checked-out' && !!asset.activeCheckoutId,
        )
        .map((asset) => String(asset.activeCheckoutId)),
    );
    if (!issuedCheckoutIds.size) {
      return;
    }

    this.reservations.update((list) =>
      list.map((reservation) => {
        if (
          reservation.pendingCheckoutId &&
          issuedCheckoutIds.has(reservation.pendingCheckoutId)
        ) {
          return { ...reservation, pendingCheckoutId: undefined };
        }
        return reservation;
      }),
    );
  }

  /** Immediate UI update after checkout / release (API refresh follows). */
  private applyLocalCheckout(
    assetId: string,
    customer?: string,
    dueDate?: string,
    reservationId?: string,
  ): void {
    const id = String(assetId);
    let updated = false;

    this.assets.update((list) => {
      const next = list.map((asset) => {
        if (asset.id !== id) {
          return asset;
        }
        updated = true;
        return {
          ...asset,
          status: 'checked-out' as const,
          pendingCheckoutId: undefined,
          reservedCustomer: customer ?? asset.reservedCustomer,
          reservedDueDate: dueDate ?? asset.reservedDueDate,
        };
      });

      if (!updated) {
        const virtual = this.checkoutDisplayAssets().find(
          (row) => row.id === id,
        );
        if (virtual) {
          next.push({
            ...virtual,
            status: 'checked-out',
            pendingCheckoutId: undefined,
            reservedCustomer: customer ?? virtual.reservedCustomer,
            reservedDueDate: dueDate ?? virtual.reservedDueDate,
          });
        }
      }

      return next;
    });

    if (reservationId) {
      this.reservations.update((list) =>
        list.map((reservation) =>
          reservation.id === reservationId
            ? { ...reservation, pendingCheckoutId: undefined }
            : reservation,
        ),
      );
    }
  }

  /** Result set 6 from GET /EquipmentCheckout — paid reservations awaiting physical issue. */
  private applyPaidWaitingQueueFromPayload(
    payload: GenericMultiResultDto,
  ): void {
    let rows = findResultSetWithColumn(
      payload,
      'QueueType',
      'PendingCheckoutID',
    ).filter(
      (row) =>
        pickNumber(row, 'PendingCheckoutID') > 0 ||
        pickString(row, 'QueueType').toUpperCase() === 'PAID_WAITING',
    );
    if (!rows.length) {
      rows = resultSetAt(payload, 5).filter(
        (row) => pickNumber(row, 'PendingCheckoutID') > 0,
      );
    }
    this.applyPaidWaitingQueueRows(rows);
  }

  /** Merge paid-waiting queue rows from GET /EquipmentCheckout/paid-waiting or result set 6. */
  private applyPaidWaitingQueueRows(rows: unknown[]): void {
    if (!rows.length) {
      return;
    }

    const queueReservations = this.mapPaidWaitingQueueRows(rows);

    this.reservations.update((list) => {
      const map = new Map(list.map((row) => [row.id, row]));
      const issued = this.issuedReservationIds();
      for (const row of queueReservations) {
        if (issued.has(row.id)) {
          // Already physically issued — do not put it back on paid-waiting.
          continue;
        }
        const existing = map.get(row.id);
        map.set(
          row.id,
          existing
            ? {
                ...existing,
                ...row,
                pendingCheckoutId:
                  row.pendingCheckoutId ?? existing.pendingCheckoutId,
                equipmentAssetID:
                  row.equipmentAssetID ?? existing.equipmentAssetID,
                customer: row.customer || existing.customer,
                endDate: row.endDate || existing.endDate,
              }
            : row,
        );
      }
      return this.filterReservationsForUser([...map.values()]);
    });

    this.assets.update((list) => {
      let changed = false;
      const next = list.map((asset) => {
        const match = queueReservations.find(
          (row) =>
            String(row.equipmentAssetID ?? '') === asset.id ||
            row.id === asset.reservationId,
        );
        if (!match?.pendingCheckoutId) {
          return asset;
        }
        if (asset.status === 'checked-out' && asset.activeCheckoutId) {
          return asset;
        }
        changed = true;
        return {
          ...asset,
          status:
            asset.status === 'checked-out' && asset.activeCheckoutId
              ? asset.status
              : ('awaiting-checkout' as const),
          pendingCheckoutId: match.pendingCheckoutId,
          activeCheckoutId:
            asset.status === 'checked-out' &&
            asset.activeCheckoutId === match.pendingCheckoutId
              ? undefined
              : asset.activeCheckoutId,
          reservationId: match.id,
          reservedCustomer: match.customer ?? asset.reservedCustomer,
          reservedDueDate: match.endDate ?? asset.reservedDueDate,
        };
      });
      return changed ? next : list;
    });
  }

  private mapPaidWaitingQueueRows(rows: unknown[]): Reservation[] {
    return rows.map((row, index) => {
      const reservationId = pickNumber(row, 'EquipmentReservationID');
      const pendingCheckoutId = pickNumber(row, 'PendingCheckoutID');
      const statusRaw = pickString(row, 'ReservationStatus').toLowerCase();

      return {
        id:
          reservationId > 0
            ? String(reservationId)
            : pendingCheckoutId > 0
              ? String(pendingCheckoutId)
              : String(index + 1),
        reservationNumber:
          pickString(row, 'ReservationNumber', 'reservationNumber') ||
          `RES-${index + 1}`,
        customer: pickString(row, 'Customer', 'customer'),
        equipment: pickString(row, 'Equipment', 'equipment') || 'Equipment',
        branch: pickString(row, 'BranchName', 'branch'),
        location: pickString(row, 'LocationName', 'location'),
        startDate: pickDate(row, 'StartDate', 'startDate'),
        endDate: pickDate(row, 'EndDate', 'endDate'),
        status: statusRaw.includes('issued')
          ? ('issued' as const)
          : statusRaw.includes('confirm')
            ? ('confirmed' as const)
            : statusRaw.includes('complete')
              ? ('completed' as const)
              : ('completed' as const),
        equipmentAssetID: pickNumber(row, 'EquipmentAssetID') || undefined,
        inventoryItemID: pickNumber(row, 'InventoryItemID') || undefined,
        pendingCheckoutId:
          pendingCheckoutId > 0 ? String(pendingCheckoutId) : undefined,
      };
    });
  }

  private filterAssets(
    assets: EquipmentAsset[],
    query: ListQuery,
  ): EquipmentAsset[] {
    const search = query.search?.trim().toLowerCase();
    if (!search) {
      return assets;
    }

    return assets.filter(
      (asset) =>
        asset.assetId.toLowerCase().includes(search) ||
        asset.name.toLowerCase().includes(search) ||
        asset.category.toLowerCase().includes(search),
    );
  }

  private paginate<T>(items: T[], query: ListQuery): PaginatedResult<T> {
    const page = Math.max(1, query.page ?? 1);
    const pageSize = Math.max(1, query.pageSize ?? 25);
    const start = (page - 1) * pageSize;
    return {
      items: items.slice(start, start + pageSize),
      total: items.length,
      page,
      pageSize,
    };
  }
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
